<script>
	import { onMount, onDestroy, tick } from 'svelte';
	import { goto } from '@roxi/routify';
	import SvelteTable from "svelte-table";
	import { getGETAPILink, getData, postFormData, capitalizeFirstLetter, postJsonData, deleteData } from "../helpers.js";
	import { clampDate, MIN_DATE, MAX_DATE } from '../dateGuard.js';
	import Navbar from "../components/Navbar.svelte";
	import Footer from "../components/Footer.svelte";
	import DateTypeInput from "../components/DateTypeInput.svelte";
	import { subscriptionType, loadFeatures } from "../stores/integrations.js";
	import { lifetimeValid, loadUserInfo } from "../stores/userInfo.js";

	let useOCR = false;
	let showSubmitDropdown = false;
	
	let [dirProjectIdError, pwcrNumberError, awardingBodyError, contractorError, projectNameError, countyError, startDateError, 
		endDateError, sortByError, sepNPError, scrapePDFsError, includePDFsError, generateContractorTreeError, limitBypassError, 
		useV2ParserError, localSearchError, privateCPRFeatureError, sensoryError, aiError] = 
		['', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''];
	let [dirProjectId, pwcrNumber, awardingBody, contractor, projectName, county, sortByInput] = ['', '', '', '', '', '', ''];
	let [sepNP, useV2Parser, scrapePDFs, includePDFs] = [false, true, false, false];
	let limitBypass = false;
	// Contractor tree is folded into the CSV Report checkbox; only sent when the paywall feature is enabled
	$: generateContractorTree = scrapePDFs && contractorTreeFeature;
	let [localSearch, privateCPR, sensoryPR, aiPR] = [true, false, false, false];
	let today = new Date()
	let todayString = `${today.getFullYear().toString().padStart(4, '0')}-${(today.getMonth()+1).toString().padStart(2, '0')}-${(today.getDate()).toString().padStart(2, '0')}`
	let [startDate, endDate] = ['2022-05-01', todayString];
	let eggMessage = '';
	let [bcStart, bcEnd] = ['', ''];
	const guardTimers = {};
	function guardField(e, key) {
		clearTimeout(guardTimers[key]);
		const target = e.target;
		guardTimers[key] = setTimeout(() => {
			const v = clampDate(target.value);
			target.value = v;
			if (key === 'cpr-start') startDate = v;
			else endDate = v;
		}, 1200);
	}
	function guardValue(value, key) {
		clearTimeout(guardTimers[key]);
		guardTimers[key] = setTimeout(() => {
			const v = clampDate(value);
			if (key === 'cpr-start') startDate = v;
			else endDate = v;
		}, 1200);
	}
	function sliverInput(e) {
		const v = e.target.value === '-' ? '-' : '';
		e.target.value = v;
		return v;
	}

	let searchState = 'preview-search'; // default: preview-search
	let downloadStartTime = null;
	let res = null;

	let recentSearches = new Map();
	const SEARCH_DEDUP_MS = 5 * 60 * 1000;
	function getSearchFingerprint() {
		return `${dirProjectId}|${pwcrNumber}|${awardingBody}|${contractor}|${projectName}|${county}|${startDate}|${endDate}|${localSearch}|${privateCPR}`;
	}

	let filteredPreviewData = [];
	let tableFilterSelections = {};

	// Get enabled paywall feature-set
	let [contractorTreeFeature, hiddenFeature, limitBypassFeature,
	localSearchFeature, privateCPRFeature, scrapePDFsFeature, sensoryFeature, aiFeature] = [false, false,false, false, false, false, false, false];
	let sepNPFeature = false;

	getData('user/paywall-features').then(response => response.json()).then(ret_json => {
		if (ret_json.status === 'success') {
			hiddenFeature = false
			
			limitBypassFeature = ret_json.features.includes('BYPASS_250_LIMIT');
			limitBypass = limitBypassFeature;
			contractorTreeFeature = ret_json.features.includes('GENERATE_CONTRACTOR_TREE');
			scrapePDFsFeature = ret_json.features.includes('CSV_REPORT');
			// scrapePDFs stays at its default (off): the feature entitlement
			// enables the checkbox but no longer pre-checks it.
			sepNPFeature = ret_json.features.includes('Separate_NonPerformance_Statements');
			sepNP = sepNPFeature;
			//apprenticeRatio = false
			//company reports = false
			//Search_Records = false
			//PWC100_Records = false

			localSearchFeature = ret_json.features.includes('CPR_SEARCH');
			privateCPRFeature = ret_json.features.includes('PRIVATE_SEARCH');
			privateCPR = privateCPRFeature;
			sensoryFeature = false
			aiFeature = false
		} else {
			otherError = 'Unknown Error Occurred. Check Connection?';
		}
	});

	// --- DIR portal login (customer's own dir.ca.gov account) ---
	// Configured credentials give the backend scraper authenticated, unthrottled
	// access; without them searches fall back to the public portal rate limit.
	let showDirLogin = false;
	let dirCredConfigured = false;
	let dirCredUsername = '';
	let dirCredStored = true;
	let [dirLoginUser, dirLoginPass] = ['', ''];
	let dirLoginRemember = true;
	let dirLoginError = '';
	let dirLoginBusy = false;

	function loadDirCredStatus() {
		getData('user/dir-credentials').then(r => r.json()).then(j => {
			if (j.status === 'success') {
				dirCredConfigured = !!j.configured;
				dirCredUsername = j.username || '';
				dirCredStored = !!j.stored;
			}
		}).catch(() => {});
	}
	loadDirCredStatus();

	function openDirLogin() {
		dirLoginUser = dirCredConfigured ? dirCredUsername : '';
		dirLoginPass = '';
		dirLoginRemember = dirCredConfigured ? dirCredStored : true;
		dirLoginError = '';
		showDirLogin = true;
	}

	function saveDirLogin() {
		if (!dirLoginUser.trim() || !dirLoginPass) {
			dirLoginError = 'Username and password are required.';
			return;
		}
		dirLoginBusy = true;
		postJsonData('user/dir-credentials', {
			username: dirLoginUser.trim(),
			password: dirLoginPass,
			store: dirLoginRemember,
		}).then(r => r.json()).then(j => {
			if (j.status === 'success') {
				showDirLogin = false;
				dirLoginPass = '';
				loadDirCredStatus();
			} else {
				dirLoginError = typeof j.error === 'string' ? j.error : 'Could not save credentials.';
			}
			dirLoginBusy = false;
		}).catch(() => {
			dirLoginError = 'Could not save credentials. Check connection?';
			dirLoginBusy = false;
		});
	}

	function removeDirLogin() {
		dirLoginBusy = true;
		deleteData('user/dir-credentials').then(r => r.json()).then(() => {
			showDirLogin = false;
			dirLoginPass = '';
			loadDirCredStatus();
			dirLoginBusy = false;
		}).catch(() => {
			dirLoginError = 'Could not remove credentials. Check connection?';
			dirLoginBusy = false;
		});
	}

	let otherError = '';
	let statusWarning = '';
	let bundleNotice = '';
	let previewDataNotice = ''

	// Bold + enlarge only the ACTUAL runtime in backend notice text — the
	// bundle download time ("estimated download ~X minutes"), or the single
	// time in the bundle-canceled notice. The discarded individual-mode
	// estimate stays unstyled. Escapes HTML first; strings are backend-controlled.
	function boldNoticeTimes(text) {
		const esc = String(text || '')
			.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
		if (esc.includes('Bundle mode canceled')) {
			return esc.replace(/(~\d+(?:\.\d+)?\s*minutes?)/g,
				'<strong class="text-base">$1</strong>');
		}
		return esc.replace(/(estimated download )(~\d+(?:\.\d+)?\s*minutes?)/g,
			'$1<strong class="text-base">$2</strong>');
	}
	let searchForm;
	let previewSubmit = false;
	// POSTs for preview data then either fills in errors or loads preview-data page. 

	let totalPreviewResults = 0;

	function handlePreview() {

		// ✅ ADD VALIDATION FIRST
		if (!localSearch && !privateCPR) {
			const proceed = confirm(
				"⚠️ No database selected.\n\n" +
				"No records will be returned.\n\n" +
				"Continue anyway?"
			);
			if (!proceed) {
				document.querySelector('.database-selector-container')
					?.scrollIntoView({ behavior: 'smooth', block: 'center' });
				return;
			}
		}
		
		console.log('handlePreview');

		// ✅ FORCE the hidden checkboxes to match the cylinder button states
		const localSearchCheckbox = document.getElementById('local-search');
		const privDbSearchCheckbox = document.getElementById('priv-db-search');
		const localSearchCheckboxMobile = document.getElementById('local-search-mobile');
		const privDbSearchCheckboxMobile = document.getElementById('priv-db-search-mobile');

		if (localSearchCheckbox) {
			localSearchCheckbox.checked = localSearch;
		}
		if (privDbSearchCheckbox) {
			privDbSearchCheckbox.checked = privateCPR;
		}
		if (localSearchCheckboxMobile) {
			localSearchCheckboxMobile.checked = localSearch;
		}
		if (privDbSearchCheckboxMobile) {
			privDbSearchCheckboxMobile.checked = privateCPR;
		}


		// ✅ USE postFormData (handles CSRF) instead of manual fetch
		previewSubmit = true;
		let res = postFormData('ecpr-search/preview', searchForm);

		//let res = postFormData('ecpr-search/preview', searchForm);
		//previewSubmit = true;
		res.then(response => response.json())
		.then(ret_json => {
			if (ret_json.status === 'success') {
				otherError = '';
				if (ret_json.entries.status === 'success') {
					if (ret_json.entries.bundle_blocked && ret_json.entries.bundle_error) {
						otherError = ret_json.entries.bundle_error;
						previewDataNotice = '';
						previewSubmit = false;
						return;
					}
					previewData = ret_json.entries.data.map((row, index) => {
						row.id = index;
						return row;
					});
					selectedIds = new Set();
					tableFilterSelections = {};
					previewDataNotice = '';
					if (ret_json.entries.bundle_warning) {
						previewDataNotice = ret_json.entries.bundle_warning;
					}
					// Suppress the generic "Showing X of Y results..." server notice: the
					// count line already shows loaded-of-total. Keep other notices
					// (e.g. permissions-filtered messages).
					if (ret_json.entries.message && !/^Showing \d+ of \d+/i.test(ret_json.entries.message)) {
						previewDataNotice = ret_json.entries.message;
					}
					totalPreviewResults = ret_json.entries.total_results || previewData.length;
					httpEstimateAnticipated = Number(ret_json.entries.http_estimate && ret_json.entries.http_estimate.anticipated) || 0;
					searchState = 'preview-data';
				} else if (ret_json.entries.status === 'error') {
					otherError = ret_json.entries.error;
					previewData = [];
					totalPreviewResults = 0;
					httpEstimateAnticipated = 0;
					selectedIds = new Set();
					tableFilterSelections = {};
					previewDataNotice = '';
					dirProjectIdError = '';
					pwcrNumberError = '';
					awardingBodyError = '';
					contractorError = '';
					projectNameError = '';
					countyError = '';
					startDateError = '';
					endDateError = '';
					sortByError = '';
					sepNPError = '';
				} else {
					otherError = 'An unknown error has occured. Please try again.';
				}
			} else if (ret_json.status === 'error') {
				if (ret_json.error === 'Account not valid.') {
					otherError = 'Billing not entered, please update payment information';
				} else if (typeof ret_json.error === 'string') {
					otherError = ret_json.error;
				} else {
					otherError = '';
					let errors = ret_json.error;
					dirProjectIdError = errors.proj_id ? errors.proj_id[0] : '';
					pwcrNumberError = errors.pwcr_num ? errors.pwcr_num[0] : '';
					awardingBodyError = errors.awarding_body ? errors.awarding_body[0] : '';
					contractorError = errors.contractor ? errors.contractor[0] : '';
					projectNameError = errors.proj_name ? errors.proj_name[0] : '';
					countyError = errors.county ? errors.county[0] : '';
					startDateError = errors.start_date ? errors.start_date[0] : '';
					endDateError = errors.end_date ? errors.end_date[0] : '';
					sortByError = errors.sort_by ? errors.sort_by[0] : '';
					sepNPError = errors.sep_np ? errors.sep_np[0] : '';
				}
			} else {
				otherError = 'An unknown error has occured. Please try again.';
			}
			previewSubmit = false;
		}).catch(() => {
			otherError = 'An unknown error has occured. Please try again.';
			previewSubmit = false;
		});
	}


	// BEGIN PREVIEW CODE 
	let previewData = [];
	// storageGet/storageSet are function declarations (hoisted), safe to call here.
	let hideNPS = storageGet('cprSearch.hideNPS', true) === true;
	$: storageSet('cprSearch.hideNPS', hideNPS);
	$: displayedPreviewData = hideNPS ? previewData.filter(r => r.statementOfNp !== "true") : previewData;

	const COLUMNS = {
		id: {
			key: "id",
			value: v => v.id,
		},
		submissionDate: {
			key: "submissionDate",
			title: "Submission",
			value: v => v.submissionDate,
			sortable: true,
			filterOptions: rows => {
				let dates = {};
				rows.forEach(row => {
				let date = row.submissionDate;
				if (dates[date] === undefined)
					dates[date] = {
					name: date,
					value: date
					};
				});

				dates = Object.entries(dates)
				.sort()
				.reduce((o, [k, v]) => ((o[k] = v), o), {});
				return Object.values(dates);
			},
			filterValue: v => v.submissionDate,
			class: "whitespace-nowrap",
			headerClass: "bg-gray-200",
			headerFilterClass: "pb-2",
		},
		forWeekEnding: {
			key: "forWeekEnding",
			title: "Week Ending",
			value: v => v.forWeekEnding,
			sortable: true,
			filterOptions: rows => {
				let dates = {};
				rows.forEach(row => {
				let date = row.forWeekEnding;
				if (dates[date] === undefined)
					dates[date] = {
					name: date,
					value: date
					};
				});
				dates = Object.entries(dates)
				.sort()
				.reduce((o, [k, v]) => ((o[k] = v), o), {});
				return Object.values(dates);
			},
			filterValue: v => v.forWeekEnding,
			class: "whitespace-nowrap",
			headerClass: "bg-gray-200",
			headerFilterClass: "pb-2",
		},
		projectID: {
			key: "projectID",
			title: "DIR Proj. ID",
			value: v => v.projectID,
			sortable: true,
			filterOptions: rows => {
				let ids = {};
				rows.forEach(row => {
				let id = row.projectID;
				if (ids[id] === undefined)
					ids[id] = {
					name: id,
					value: id
					};
				});
				ids = Object.entries(ids)
				.sort()
				.reduce((o, [k, v]) => ((o[k] = v), o), {});
				return Object.values(ids);
			},
			filterValue: v => v.projectID,
			headerClass: "bg-gray-200",
			headerFilterClass: "pb-2",
		},
		projectName: {
			key: "projectName",
			title: "Project",
			value: v => v.projectName,
			sortable: true,
			headerClass: "bg-gray-200",
		},
		awardingBody: {
			key: "awardingBody",
			title: "Awarding Body",
			value: v => v.awardingBody,
			sortable: true,
			headerClass: "bg-gray-200",
		},
		contractorName: {
			key: "contractorName",
			title: "Contractor",
			value: v => v.contractorName,
			sortable: true,
			headerClass: "bg-gray-200",
		},
		contractorPWCR: {
			key: "contractorPWCR",
			title: "PWCR",
			value: v => v.contractorPWCR,
			sortable: true,
			headerClass: "bg-gray-200",
		},
		payrollNum: {
			key: "payrollNum",
			title: "Payroll Number",
			value: v => v.payrollNum,
			// amended payrolls (psql_search.py:167 payrollAmendNum) show "n-amend"
			// suffix when nonzero; bundle-content rows (psql_search.py:174
			// bundle_record flag) show "-bundle" suffix; value stays bare so
			// sorting is unaffected
			renderValue: v => {
				const num = v.payrollAmendNum && v.payrollAmendNum !== '0' ? `${v.payrollNum}-${v.payrollAmendNum}` : v.payrollNum;
				return v.bundle_record ? `${num}-bundle` : num;
			},
			sortable: true,
			class: "text-center",
			headerClass: "bg-gray-200 mt-2",
		},
		county: {
			key: "county",
			title: "County",
			value: v => v.county || "N/A",
			sortable: true,
			headerClass: "bg-gray-200",
		},
	};
	let selectedDesktopCols = ["contractorPWCR", "forWeekEnding", "submissionDate", "projectName", "projectID", "awardingBody", "contractorName", "county", "payrollNum"];
	$: selectCol = {
		key: "select",
		title: "",
		value: v => (selectedIds.has(v.id) ? 1 : 0),
		renderValue: v => `<input type="checkbox" ${selectedIds.has(v.id) ? "checked" : ""} style="pointer-events:none; width:1.1rem; height:1.1rem;">`,
		parseHTML: true,
		sortable: false,
		headerClass: "bg-gray-200",
	};
	$: desktopCols = [selectCol, ...selectedDesktopCols.map(key => COLUMNS[key])];
	let selectedMobileCols = ["forWeekEnding", "contractorName", "payrollNum"];
	$: mobileCols = selectedMobileCols.map(key => COLUMNS[key]);

	let expandedArr = [];
	let selectedIds = new Set();
	function toggleSelected(id) {
		if (selectedIds.has(id)) { selectedIds.delete(id); } else { selectedIds.add(id); }
		selectedIds = new Set(selectedIds); // reassign so Svelte re-renders the column
	}


	let searchId = '';
	let searchSubmit = false;

	// Per-file fee approval: non-MONTHLY, non-lifetime accounts pay $0.10 per
	// file. The accepted estimate is a billing cap — the backend charges
	// min(actual file_count, approved_file_count) (ecpr_core_service.py).
	loadFeatures();
	loadUserInfo();
	const PER_FILE_FEE = 0.10;
	$: feeLiable = $subscriptionType !== '' && $subscriptionType !== 'MONTHLY' && !$lifetimeValid;
	$: feeCheckedRows = previewData.filter(r => selectedIds.has(r.id));
	$: feeEffectiveRows = feeCheckedRows.length > 0 ? feeCheckedRows
		: (filteredPreviewData.length > 0 && filteredPreviewData.length < displayedPreviewData.length ? filteredPreviewData : []);
	// Preview rows are (PID, contractor) pairs unless they carry a fileId, so
	// the row count is only trusted when every effective row is a single file.
	// Otherwise use the backend record estimate, clipped to the 250-record cap
	// that applies to non-BYPASS accounts.
	$: approvedFileCount = feeEffectiveRows.length > 0 && feeEffectiveRows.every(r => r.fileId)
		? feeEffectiveRows.length
		: (httpEstimateAnticipated > 0 ? Math.min(httpEstimateAnticipated, 250) : 250);
	$: approvedFee = (approvedFileCount * PER_FILE_FEE).toFixed(2);
	let acceptedFileCount = 0; // captured at submit for the finished-state receipt

	async function handleSearch() {
		console.log('handleSearch');
		console.log('filteredPreviewData:', filteredPreviewData);
		console.log('filteredPreviewData.length:', filteredPreviewData.length);
		console.log('previewData.length:', previewData.length);

		// Selection passback: checked rows win; else active dropdown filter; else empty = no restriction
		const checkedRows = previewData.filter(r => selectedIds.has(r.id));
		let effectiveRows = [];
		if (checkedRows.length > 0) {
			effectiveRows = checkedRows;
		} else if (filteredPreviewData.length > 0 && filteredPreviewData.length < displayedPreviewData.length) {
			effectiveRows = filteredPreviewData;
		}
		const selFileIds = JSON.stringify([...new Set(effectiveRows.filter(r => r.fileId).map(r => r.fileId))]);
		const selPids = JSON.stringify([...new Set(effectiveRows.filter(r => !r.fileId).map(r => String(r.projectID)))]);
		for (const elId of ['selected-file-ids', 'selected-file-ids-mobile']) {
			const el = document.getElementById(elId);
			if (el) el.value = selFileIds;
		}
		for (const elId of ['selected-pids', 'selected-pids-mobile']) {
			const el = document.getElementById(elId);
			if (el) el.value = selPids;
		}
		// Fee-approval cap passback (billing maximum accepted by the user)
		acceptedFileCount = feeLiable ? approvedFileCount : 0;
		for (const elId of ['approved-file-count', 'approved-file-count-mobile']) {
			const el = document.getElementById(elId);
			if (el) el.value = feeLiable ? String(approvedFileCount) : '';
		}

		// Align form criteria with the rows actually being restricted to
		// (checked rows or active dropdown filter). When effectiveRows is
		// empty the user is downloading everything the search matched, so
		// their typed criteria must pass through unchanged — narrowing from
		// the visible preview would drop rows hidden by the NP-statement
		// filter (their week endings are excluded from the min/max).
		if (effectiveRows.length > 0) {
			// Extract unique project IDs
			const uniqueProjectIds = [...new Set(effectiveRows.map(row => row.projectID))];
			if (uniqueProjectIds.length === 1) {
				dirProjectId = uniqueProjectIds[0];
				// Update both desktop and mobile forms
				document.getElementById('dir-project-id').value = uniqueProjectIds[0];
				document.getElementById('dir-project-id-mobile').value = uniqueProjectIds[0];
			}

			const uniqueContractors = [...new Set(effectiveRows.map(row => row.contractorName))];
			if (uniqueContractors.length === 1) {
				contractor = uniqueContractors[0];
				document.getElementById('contractor').value = uniqueContractors[0];
				document.getElementById('contractor-mobile').value = uniqueContractors[0];
			}
			
			const uniqueAwardingBodies = [...new Set(effectiveRows.map(row => row.awardingBody))];
			if (uniqueAwardingBodies.length === 1) {
				awardingBody = uniqueAwardingBodies[0];
				document.getElementById('awarding-body').value = uniqueAwardingBodies[0];
				document.getElementById('awarding-body-mobile').value = uniqueAwardingBodies[0];
			}
			
			// Extract date range from forWeekEnding
			const dates = effectiveRows.map(row => row.forWeekEnding).filter(d => d);
			if (dates.length > 0) {
				startDate = dates.reduce((min, date) => date < min ? date : min);
				endDate = dates.reduce((max, date) => date > max ? date : max);
				// Update both desktop and mobile forms
				document.getElementById('start-date').value = startDate;
				document.getElementById('end-date').value = endDate;
			}

			// Wait for Svelte to update the DOM form inputs
			await tick();

		}

		/*
		console.log('Before postFormData - checking form values:');
		console.log('Desktop proj_id:', document.getElementById('dir-project-id').value);
		console.log('Mobile proj_id:', document.getElementById('dir-project-id-mobile').value);
		console.log('Desktop start_date:', document.getElementById('start-date').value);
		console.log('Mobile start_date:', document.getElementById('start-date-mobile').value);
		console.log('dirProjectId variable:', dirProjectId);
		console.log('startDate variable:', startDate);
		*/

		searchSubmit = true;
		let fp = getSearchFingerprint();
		let lastTime = recentSearches.get(fp);
		if (lastTime && (Date.now() - lastTime < SEARCH_DEDUP_MS)) {
			let ago = Math.round((Date.now() - lastTime) / 1000);
			otherError = `You already submitted this search ${ago}s ago — it's processing.`;
			searchSubmit = false;
			return;
		}
		recentSearches.set(fp, Date.now());


		// Force use_ocr checkbox to match current state before form submission
		const ocrCheckbox = document.getElementById('use-ocr');
		if (ocrCheckbox) ocrCheckbox.checked = useOCR;
		const ocrCheckboxMobile = document.getElementById('use-ocr-mobile');
		if (ocrCheckboxMobile) ocrCheckboxMobile.checked = useOCR;

		console.log('ecpr-search/submit');
		res = postFormData('ecpr-search/submit', searchForm);

		res.then(response => response.json())
		.then(ret_json => {
			if (ret_json.status === 'success') {
				otherError = '';
				statusWarning = '';
				bundleNotice = effectiveRows.length > 0 ? '' : previewDataNotice;
				searchId = ret_json.search_id;
				searchState = 'aggregating-results';
				downloadedFiles = 0; // reset high-water mark for the new search
				successFileCount = null;
				totalFiles = 0;
				// Backend record estimate only (http_estimate.anticipated, from
				// _estimate_pid_records). Never the preview row count — rows are
				// (PID, contractor) pairs, not records. 0 => "total being determined".
				estimatedTotalFiles = httpEstimateAnticipated;
				dirScopeTotal = 0;
				downloadStartTime = null;
				setTimeout(updateSearchStatus(searchId), 750); // Prevent Invalid Search ID
			} else if (ret_json.status === 'error') {
				if (ret_json.error === 'Account not valid.') {
					otherError = 'Billing not entered, please update payment information';
				} else if (typeof ret_json.error === 'string') {
					otherError = ret_json.error;
				} else {
					otherError = 'Error(s) occured, please fix before continuing';
					let errors = ret_json.error;
					dirProjectIdError = errors.proj_id ? errors.proj_id[0] : '';
					pwcrNumberError = errors.pwcr_num ? errors.pwcr_num[0] : '';
					awardingBodyError = errors.awarding_body ? errors.awarding_body[0] : '';
					contractorError = errors.contractor ? errors.contractor[0] : '';
					projectNameError = errors.proj_name ? errors.proj_name[0] : '';
					countyError = errors.county ? errors.county[0] : '';
					startDateError = errors.start_date ? errors.start_date[0] : '';
					endDateError = errors.end_date ? errors.end_date[0] : '';
					sortByError = errors.sort_by ? errors.sort_by[0] : '';
					sepNPError = errors.sep_np ? errors.sep_np[0] : '';
				}
			} else {
				otherError = 'An unknown error has occured. Please try again.';
			}
			searchSubmit = false;
		}).catch(() => {
			otherError = 'An unknown error has occured. Please try again.';
			searchSubmit = false;
		});
	}

	let searchStatus = 'downloading';
	let downloadedFiles = 0;
	let successFileCount = null;
	$: deliveredFileCount = successFileCount !== null ? successFileCount : downloadedFiles;
	let totalFiles = 0;
	// Backend record estimate (http_estimate.anticipated) captured at preview
	// time. Shown as "~N (estimate)" until the backend commits the real
	// file_count (ecpr_io.py:285) and the status poll returns a number.
	let estimatedTotalFiles = 0;
	// Exact job-wide DIR count (scope_total from the status poll,
	// common_search_endpoints.py check_status). 0 until the backend has
	// counted every search window (plugin_ecpr_http_dir_pull.py
	// _search_resolved).
	let dirScopeTotal = 0;
	let httpEstimateAnticipated = 0;
	let statusTimeoutID;

	// Rotating status blurbs: liveness indicator during the long download phase.
	// Pool is picked per tick based on download progress, so the copy tracks
	// the phase (startup, steady downloading, stalls between counts, wrap-up).
	const startBlurbs = [
		'Connecting to the DIR server\u2026',
		'Finding the edges of each project\u2019s start and end dates so no payroll record is missed\u2026',
		'Estimating start and end dates where missing in DIR records\u2026',
		'Estimating start date\u2026',
		'Estimating end date\u2026',
		'Estimating project timeframes where DIR dates are missing or are plug values\u2026',
		'Deriving county and city where DIR records carry no location\u2026',
		'Locating payroll records across the project date range\u2026',
	];
	// Educational facts: shared by the steady rotation and the stall state
	// (stall leads with "Still working" lines, then lists facts while stalled).
	const factBlurbs = [
		'Every contractor and subcontractor files its own certified payroll directly with the Labor Commissioner (Labor Code 1771.4)\u2026',
		'Even sole proprietors must file \u2014 owners are required to pay themselves prevailing wage (Labor Code 1774)\u2026',
		'Prevailing wage applies to nearly all public works over $1,000 (Labor Code 1771)\u2026',
		'Wage rates are set per craft and county by the DIR Director (Labor Code 1773)\u2026',
		'Eight hours is a legal day\u2019s work \u2014 beyond that means time-and-a-half (Labor Code 1810, 1815)\u2026',
		'Underpaying prevailing wage costs up to $200 per worker per day in penalties (Labor Code 1775)\u2026',
		'Certified payroll records are mandatory \u2014 $100 per worker per day for failing to furnish them (Labor Code 1776)\u2026',
		'Wage claims go to the Labor Commissioner \u2014 a Berman hearing ends in an enforceable award (Labor Code 98)\u2026',
		'On public works, DLSE issues Civil Wage and Penalty Assessments against the contractor and its surety (Labor Code 1741)\u2026',
		'PAGA lets workers sue on behalf of the state for Labor Code violations (Labor Code 2698)\u2026',
		'Common case: workers misclassified into a lower-paid craft or as independent contractors\u2026',
		'Common case: missed meal and rest breaks (Labor Code 226.7)\u2026',
		'Kickbacks \u2014 forcing workers to hand back wages \u2014 are a crime (Labor Code 1778)\u2026',
		'\u201CEvery worker deserves to be paid fully and fairly for their work.\u201D \u2014 CA Attorney General Rob Bonta',
		'California workers lose about $2 billion a year to wage theft (CA DOJ)\u2026',
		'The federal Davis\u2013Bacon Act set prevailing wages in 1931 \u2014 after 14 failed attempts in Congress\u2026',
		'Kansas passed the nation\u2019s first prevailing wage law in 1891, forty years before Davis\u2013Bacon\u2026',
		'1891 twice over: the same year as Kansas, Britain\u2019s Parliament adopted the Fair Wages Resolution for government contracts\u2026',
		'Britain\u2019s Fair Wages Resolution held for nearly a century \u2014 1891 to 1983\u2026',
		'ILO Convention 94 (1949) took the idea worldwide \u2014 public contracts in 62 countries must match local wage standards\u2026',
		'The Code of Hammurabi fixed daily wages for hired laborers nearly 4,000 years ago\u2026',
		'Athens, 408 BC: the Erechtheion temple\u2019s building accounts \u2014 payroll records in stone \u2014 paid citizen, foreigner, and slave the same drachma a day\u2026',
		'\u201CYou shall give him his wages on the same day, before the sun sets\u2026\u201D \u2014 Deuteronomy 24:15',
		'Rome, 301 AD: Diocletian\u2019s Edict set maximum wages by craft across the empire\u2026',
		'\u201CWhich Side Are You On?\u201D was written by Florence Reece during the 1931 Harlan County coal strike \u2014 the same year Davis\u2013Bacon passed\u2026',
		'\u201CTo rise in the world he carried a hod\u2026\u201D \u2014 Tim Finnegan of the old Irish ballad was a construction laborer; today he\u2019d be on a certified payroll\u2026',
		'Projects under a qualifying project labor agreement can be exempt from eCPR filing at the Labor Commissioner\u2019s discretion \u2014 one reason records may be missing\u2026',
		'California\u2019s prevailing rates usually track the union rate \u2014 DIR adopts the modal rate from collective bargaining agreements\u2026',
		'Since 2024, federal construction projects of $35 million or more must use a project labor agreement (Executive Order 14063)\u2026',
		'Public funds in any form can trigger prevailing wage \u2014 fee waivers, below-market land sales, and below-market loans all count (Labor Code 1720(b))\u2026',
		'Private project, public strings: subdivision streets, sewers, and easement improvements built for dedication to a public agency can be public works\u2026',
		'California\u2019s streamlined housing laws (SB 35, AB 2011) attach prevailing wage \u2014 and certified payroll \u2014 to otherwise fully private housing projects\u2026',
		'A prevailing wage is a package: base pay plus health, pension, vacation, and apprenticeship training (Labor Code 1773.1) \u2014 no benefit plan means the full amount is owed in cash\u2026',
		'The health & welfare fringe keeps construction families insured \u2014 hour-banked plans carry coverage through the gaps between jobs\u2026',
		'The pension fringe builds retirement hour by hour \u2014 portable multi-employer plans are often a trade worker\u2019s only retirement savings\u2026',
		'The training fund fringe finances apprenticeship \u2014 every prevailing wage hour helps pay to train the next generation of the skilled trades\u2026',
		'Training funds also upskill the existing workforce \u2014 retraining journeymen for new technology and a shifting portfolio of in-demand skills\u2026',
		'California\u2019s $1,000 prevailing wage threshold is among the nation\u2019s lowest \u2014 22 states have none at all\u2026',
		'eCPR.io is built by Palo Alto Data Group \u2014 turning DIR\u2019s public records into fast, searchable, exportable results\u2026',
		'PADG has recovered over 1,300 contractor\u2013project links dropped by DIR\u2019s 2024 migration\u2026',
		'PADG models the labor a project should require and compares it against what was actually reported\u2026',
		'PADG maintains a wage theft score for every company \u2014 a credit-score-like measure of compliance reliability\u2026',
		'Custom investigations and advanced data services: information@paloaltodatagroup.com',
	];
	const steadyOpsBlurbs = [
		'Requesting PDFs from DIR one project at a time\u2026',
		'Filtering out statements of non-performance\u2026',
		'Finding contractors not listed on PWC100\u2026',
		'Walking each project\u2019s full start\u2013stop date range\u2026',
		'The total isn\u2019t known until the last record is found \u2014 the counter keeps ticking\u2026',
		'Splitting large projects into smaller date-range chunks for reliable pulls\u2026',
		'Identifying each PDF\u2019s format \u2014 DLSE, LCP, WH-347\u2026',
		'Matching contractor name variations the DIR search misses\u2026',
		'Tracking amended payrolls \u2014 DIR keeps every version; we identify the latest\u2026',
		'Recovering contractor links dropped in DIR\u2019s 2024 migration\u2026',
	];
	const stallLeadBlurbs = [
		'Still working \u2014 large projects can take a minute between records\u2026',
		'A large project is being split into smaller date ranges and re-pulled \u2014 the counter resumes shortly\u2026',
		'Contractors file monthly \u2014 the newest weeks may not be on DIR yet (Labor Code 1771.4)\u2026',
	];
	const steadyBlurbs = [...steadyOpsBlurbs, ...factBlurbs];
	const stallBlurbs = [...stallLeadBlurbs, ...factBlurbs];
	const endBlurbs = [
		'Almost done \u2014 staging downloaded files for packaging\u2026',
		'Preparing your results archive\u2026',
	];
	const STALL_THRESHOLD_MS = 15000;
	const BLURB_TICK_MS = 4000;
	// Steady pool rotates alongside a moving counter, which already shows
	// liveness — so it advances much slower than the other pools.
	const STEADY_ADVANCE_MS = 20000;
	// Stall facts are longer reads; slower than the 4s tick, faster than steady.
	const STALL_ADVANCE_MS = 8000;
	// Facts are lower priority than operational messages: on each advance in
	// the steady/stall pools, a fact is shown with this probability, otherwise
	// an operational/lead line is shown.
	const FACT_PROBABILITY = 0.3;
	let blurbText = startBlurbs[0];
	let blurbPool = startBlurbs;
	let blurbIntervalID = null;
	let lastCountChangeTime = Date.now();
	let lastBlurbAdvanceTime = Date.now();

	function pickBlurbPool() {
		if (downloadedFiles === 0) return startBlurbs;
		if (typeof totalFiles === 'number' && totalFiles > 0 && downloadedFiles >= totalFiles * 0.9) return endBlurbs;
		if (Date.now() - lastCountChangeTime > STALL_THRESHOLD_MS) return stallBlurbs;
		return steadyBlurbs;
	}

	function startBlurbRotation() {
		if (blurbIntervalID) return;
		lastCountChangeTime = Date.now();
		blurbPool = pickBlurbPool();
		blurbText = blurbPool[0];
		lastBlurbAdvanceTime = Date.now();
		// Tick fast so pool switches (e.g. into stall) show promptly, but only
		// advance within a pool once that pool's own cadence has elapsed.
		blurbIntervalID = setInterval(() => {
			const pool = pickBlurbPool();
			if (pool !== blurbPool) {
				blurbPool = pool;
				blurbText = pool[0];
				lastBlurbAdvanceTime = Date.now();
				return;
			}
			const advanceMs = pool === steadyBlurbs ? STEADY_ADVANCE_MS
				: pool === stallBlurbs ? STALL_ADVANCE_MS : BLURB_TICK_MS;
			if (Date.now() - lastBlurbAdvanceTime >= advanceMs) {
				// Random pick (repeats allowed): each search surfaces its own
				// subset of the pool rather than marching through all of it.
				// Facts are weighted below operational/lead lines.
				const primary = pool === steadyBlurbs ? steadyOpsBlurbs
					: pool === stallBlurbs ? stallLeadBlurbs : pool;
				const source = (primary !== pool && Math.random() < FACT_PROBABILITY)
					? factBlurbs : primary;
				blurbText = source[Math.floor(Math.random() * source.length)];
				lastBlurbAdvanceTime = Date.now();
			}
		}, BLURB_TICK_MS);
	}

	function stopBlurbRotation() {
		if (blurbIntervalID) {
			clearInterval(blurbIntervalID);
			blurbIntervalID = null;
		}
	}
	onDestroy(stopBlurbRotation);

	// Consecutive-failure tolerance for the status poll. A single failed poll
	// (network blip, transient 502, momentary error reply) retries on the next
	// tick; only an unbroken run of failures stops polling. (Transient-failure
	// freeze diagnosed 2026-07-26.)
	let pollFailureCount = 0;
	const MAX_POLL_FAILURES = 10; // ~50s of consecutive failures before giving up

	function retryStatusPoll(searchId, reason) {
		pollFailureCount += 1;
		console.log(`status poll failure ${pollFailureCount}/${MAX_POLL_FAILURES}:`, reason);
		if (pollFailureCount < MAX_POLL_FAILURES) {
			statusTimeoutID = setTimeout(updateSearchStatus, 5000, searchId);
		} else {
			stopBlurbRotation();
			searchStatus = 'error';
			otherError = 'Lost contact with the server. Your search continues running — see History to download the results.';
		}
	}

	function updateSearchStatus(searchId) {
		console.log('updateSearchStatus');
		let res = getData('ecpr-search/status', {search_id: searchId});
		res.then(response => response.json())
		.then(ret_json => {
			if (ret_json.status === 'success') {
				searchStatus = ret_json.search_status;
				statusWarning = ret_json.warning || '';
				if (ret_json.warning && /bundle packet/i.test(ret_json.warning)) {
					bundleNotice = ret_json.warning;
				}
				// Backend-controlled phrase (dir_login_notice.txt via check_status):
				// a rejected DIR login means the pull fell back to the public guest
				// tier, so drop the green "Unthrottled" button state to match.
				if (ret_json.warning && /DIR login was rejected/i.test(ret_json.warning)) {
					dirCredConfigured = false;
				}
				// The revised DIR scope count travels only inside the notice text
				// (bundle_notice.txt via common_search_endpoints.py:123-128) — the
				// poll has no numeric field for it. Parse the backend-controlled
				// phrase (bundle_pull_utils.py: "approximately N records in scope"),
				// same pattern as the "~X minutes" highlight above.
				const scopeMatch = (ret_json.warning || '').match(/approximately ([\d,]+) records in scope/i);
				if (scopeMatch) {
					estimatedTotalFiles = Number(scopeMatch[1].replace(/,/g, '')) || estimatedTotalFiles;
				}
				// Numeric DIR total (scope_total) — preferred over the text
				// parse above; renders plainly as "X of N" (no tilde).
				dirScopeTotal = Number(ret_json.scope_total) || dirScopeTotal;
				if (ret_json.success_file_count !== undefined && ret_json.success_file_count !== null) {
					successFileCount = ret_json.success_file_count;
				}
				if (ret_json.search_status === 'downloading') {
					pollFailureCount = 0;
					if (!downloadStartTime) downloadStartTime = Date.now();
					startBlurbRotation();
					// High-water mark: the backend counts PDFs on disk, which drops to 0
					// between the pull phase (local staging cleaned up after B2 upload)
					// and the packaging phase (files re-pulled for the zip). Never show
					// a regression below the highest count seen for this search.
					const newCount = Math.max(downloadedFiles, ret_json.downloaded_files);
					if (newCount !== downloadedFiles) lastCountChangeTime = Date.now();
					downloadedFiles = newCount;
					totalFiles = ret_json.total_files;
					statusTimeoutID = setTimeout(updateSearchStatus, 5000, searchId);
				} else if (ret_json.search_status === 'sorting') {
					pollFailureCount = 0;
					stopBlurbRotation();
					statusTimeoutID = setTimeout(updateSearchStatus, 3000, searchId); // 3 sec for delay
				} else if (ret_json.search_status === 'zipping') {
					pollFailureCount = 0;
					stopBlurbRotation();
					statusTimeoutID = setTimeout(updateSearchStatus, 2500, searchId); // 2.5 sec for delay
				} else if (ret_json.search_status === 'finished') {
					pollFailureCount = 0;
					stopBlurbRotation();
					// No callback, finished; TODO(?)
				} else if (ret_json.search_status === 'scraping') {
					pollFailureCount = 0;
					statusTimeoutID = setTimeout(updateSearchStatus, 2000, searchId); // 2.0 sec for delay
				} else {
					// Unrecognized search_status: retry instead of dying on it.
					retryStatusPoll(searchId, 'unknown search_status: ' + ret_json.search_status);
				}
			} else {
				// Server answered with an error payload: retry instead of dying on it.
				retryStatusPoll(searchId, ret_json.error);
			}
		})
		.catch(err => {
			// Network failure or non-JSON reply (e.g. 502 HTML page): retry.
			retryStatusPoll(searchId, err);
		});
	}

	let cancelSubmit = false;
	function cancelSearch(searchId) {
		console.log('cancelSearch');
		let res = postJsonData('ecpr-search/cancel', {'search_id': searchId});
		cancelSubmit = true;
		res.then(response => response.json())
		.then(ret_json => {
			if (ret_json.status === 'success') {
				clearTimeout(statusTimeoutID);
				stopBlurbRotation();
				recentSearches.delete(getSearchFingerprint());
				searchState = 'preview-search';
				previewData = []
				selectedIds = new Set();
				tableFilterSelections = {};
			} else if (ret_json.status === 'error') {
				otherError = ret_json.error;
			}
			cancelSubmit = false;
		});
	}

	function newSearch() {
		searchState = 'preview-search'; // default
		searchStatus = 'downloading'; // default
		searchId = '';
		estimatedTotalFiles = 0;
		dirScopeTotal = 0;
		httpEstimateAnticipated = 0;
		previewData = [];
		selectedIds = new Set();
		tableFilterSelections = {};
		searchSubmit = false;
		otherError = '';
		statusWarning = '';
		bundleNotice = '';
		previewDataNotice = '';
		[dirProjectId, pwcrNumber, awardingBody, contractor] = ['', '', '', '']
		[projectName, county, startDate, endDate, sortByInput] = ['', '', '', '', '']
		[dirProjectIdError, pwcrNumberError, awardingBodyError, contractorError, projectNameError, 
			countyError, startDateError, endDateError, sortByError, sepNPError, scrapePDFsError, 
			includePDFsError, generateContractorTreeError, sensoryError, aiError] = 
			['', '', '', '', '', '', '', '', '', '', '', '', '', '', ''];
	}

	// --- Collapsible search side panel (desktop preview-data view) ---
	const PANEL_OPEN_KEY = 'cprSearch.panelOpen';
	const PANEL_WIDTH_KEY = 'cprSearch.panelWidthPct';

	// Safe localStorage wrappers: browsers with site data blocked throw on
	// any localStorage access, so fall back to defaults silently.
	function storageGet(key, fallback) {
		try {
			const v = localStorage.getItem(key);
			return v === null ? fallback : JSON.parse(v);
		} catch {
			return fallback;
		}
	}
	function storageSet(key, value) {
		try {
			localStorage.setItem(key, JSON.stringify(value));
		} catch {
			// Storage blocked: feature still works, just won't persist.
		}
	}

	let panelOpen = storageGet(PANEL_OPEN_KEY, false) === true;
	let panelWidthPct = Number(storageGet(PANEL_WIDTH_KEY, 33));
	if (!Number.isFinite(panelWidthPct) || panelWidthPct < 20 || panelWidthPct > 60) {
		panelWidthPct = 33;
	}

	$: splitView = searchState === 'preview-data' && panelOpen;

	// Split-view form layout: stack field pairs into one column
	$: rowCls = splitView ? 'flex flex-col space-y-3' : 'flex space-x-4';
	$: colCls = splitView ? 'input w-full' : 'input w-1/2';

	function togglePanel() {
		panelOpen = !panelOpen;
		storageSet(PANEL_OPEN_KEY, panelOpen);
	}

	// Draggable divider between side panel and results
	let splitContainer;
	let dragging = false;
	function dividerDown(e) {
		dragging = true;
		e.target.setPointerCapture(e.pointerId);
	}
	function dividerMove(e) {
		if (!dragging || !splitContainer) return;
		const rect = splitContainer.getBoundingClientRect();
		const pct = ((e.clientX - rect.left) / rect.width) * 100;
		panelWidthPct = Math.min(60, Math.max(20, pct));
	}
	function dividerUp() {
		if (!dragging) return;
		dragging = false;
		storageSet(PANEL_WIDTH_KEY, Math.round(panelWidthPct * 10) / 10);
	}

</script>

<main class=background>
	<Navbar/>

	{#if showDirLogin}
		<div class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
			<div class="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-xl">
				<p class="text-xl font-semibold text-blue-900 mb-2">DIR Portal Login</p>
				<p class="text-sm text-gray-600 mb-4">
					Enter your own DIR (dir.ca.gov) account for unthrottled downloads.
					Without credentials, searches use the public portal and are rate-limited.
				</p>
				<p class="text-red-500 text-sm mb-2 {dirLoginError === '' ? 'hidden' : ''}">{dirLoginError}</p>
				<div class="flex flex-col space-y-3">
					<input placeholder="DIR username (email)" bind:value={dirLoginUser}
						class="rounded-md shadow-sm py-2 px-3 border border-gray-300 w-full" autocomplete="off"/>
					<input type="password" placeholder="DIR password" bind:value={dirLoginPass}
						class="rounded-md shadow-sm py-2 px-3 border border-gray-300 w-full" autocomplete="new-password"/>
					<div class="flex items-center space-x-2">
						<input id="dir-login-remember" type="checkbox" bind:checked={dirLoginRemember} class="w-5 h-5"/>
						<label for="dir-login-remember">Remember my credentials</label>
					</div>
					<p class="text-xs text-gray-500">
						Unchecked: credentials are kept only until your download session goes idle (~15 min) and are never saved.
					</p>
				</div>
				<div class="flex justify-end space-x-3 mt-6">
					{#if dirCredConfigured}
						<button type="button" on:click={removeDirLogin} disabled={dirLoginBusy}
							class="px-4 py-2 rounded-md border border-red-500 text-red-600 hover:bg-red-50 font-semibold">
							Remove
						</button>
					{/if}
					<button type="button" on:click={() => { showDirLogin = false; dirLoginPass = ''; }}
						class="px-4 py-2 rounded-md border border-gray-400 text-gray-700 hover:bg-gray-100 font-semibold">
						Cancel
					</button>
					<button type="button" on:click={saveDirLogin} disabled={dirLoginBusy}
						class="px-4 py-2 rounded-md bg-blue-500 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold">
						Save
					</button>
				</div>
			</div>
		</div>
	{/if}
	<div class="bg-amber-50 border-l-4 border-amber-400 p-4 mx-4 mt-4 text-sm text-amber-900">
		<p>
			<strong>Contractor and PWCR searches may be incomplete</strong> — we are backfilling ~5,100 projects affected by DIR's missing contractor links. If a search comes up short, try the DIR Project ID field, which has complete records.
		</p>
	</div>

	<div class="flex flex-col space-y-3 mt-6 items-center lg:flex-row lg:justify-center">
		<div class="mx-2 !mb-4 px-10 py-6 {searchState === 'preview-data' ? 'max-w-full lg:mx-6' : 'max-w-6xl'} w-full flex-1 card">
			<!-- Use hidden to preserve form state instead of discarding -->
			
			<!--DESKTOP-->
			<div class="hidden lg:block"> <!-- Cheap way out, especially since layout change on desktop-->
				<div class="{splitView ? 'flex items-start' : ''}" bind:this={splitContainer}>
				<div class="{searchState === 'preview-search' ? '' : (splitView ? 'min-w-0 overflow-y-auto max-h-[75vh] pr-3' : 'hidden')}"
					style="{splitView ? `flex: 0 0 ${panelWidthPct}%;` : ''}">
					<p class="{splitView ? 'text-lg' : 'text-2xl'} font-semibold text-blue-900 mb-4">Search for Payroll Records</p>
					<p class="text-red-500 {otherError === '' ? '' : 'text-xl pb-5'}">{otherError}</p>

					<!-- DATABASE SELECTION - Top of Page -->
					<div class="database-selector-container">
						<div class="database-icons-wrapper">
							<!-- Public Database Cylinder -->
							<button 
								type="button"
								class="database-cylinder {localSearch ? 'active' : ''} {splitView ? 'compact' : ''}"
								on:click={() => localSearch = !localSearch}
								disabled={!localSearchFeature}
							>
								<div class="cylinder-top"></div>
								<div class="cylinder-body">
									<svg class="database-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
										<ellipse cx="12" cy="5" rx="9" ry="3"></ellipse>
										<path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"></path>
										<path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path>
									</svg>
								</div>
								<div class="cylinder-label">
									<span class="db-title">{splitView ? 'Public' : 'Public db'}</span>
									{#if !splitView}<span class="db-subtitle">DLSE eCPR</span>{/if}
								</div>
								{#if localSearch}
									<div class="selection-badge">✓</div>
								{/if}
							</button>

							<!-- Private Database Cylinder -->
							<button 
								type="button"
								class="database-cylinder {privateCPR ? 'active' : ''} {splitView ? 'compact' : ''}"
								on:click={() => privateCPR = !privateCPR}
								disabled={!privateCPRFeature}
							>
								<div class="cylinder-top"></div>
								<div class="cylinder-body">
									<svg class="database-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
										<ellipse cx="12" cy="5" rx="9" ry="3"></ellipse>
										<path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"></path>
										<path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path>
										<rect x="9" y="9" width="6" height="7" rx="1"></rect>
										<path d="M12 13v2"></path>
									</svg>
								</div>
								<div class="cylinder-label">
									<span class="db-title">{splitView ? 'Private' : 'Private db'}</span>
									{#if !splitView}<span class="db-subtitle">Your Uploads</span>{/if}
								</div>
								{#if privateCPR}
									<div class="selection-badge">✓</div>
								{/if}
							</button>
						</div>
					</div>

					<form method="POST" class="h-full relative" bind:this={searchForm} on:submit|preventDefault={handlePreview}>
						<input type="hidden" id="selected-file-ids" name="selected_file_ids" value="">
						<input type="hidden" id="selected-pids" name="selected_pids" value="">
						<input type="hidden" id="approved-file-count" name="approved_file_count" value="">
						<!-- <input id="csrf_token" name="csrf_token" value="" class="hidden"> -->

						<!-- Hidden inputs for form submission -->
						<input type="checkbox" id="local-search" name="local_search" value="on" class="hidden" />
						<input type="checkbox" id="priv-db-search" name="priv_db_search" value="on" class="hidden" />
						<input type="checkbox" id="use-ocr" name="use_ocr" value="on" class="hidden" bind:checked={useOCR} />

						<div class="flex flex-col space-y-3 {splitView ? 'text-base' : 'text-xl'}">

							<div class={rowCls}>
								<div class={colCls}>
									<div class="relative">
										<input id="dir-project-id" name="proj_id" bind:value={dirProjectId} placeholder="DIR Project ID" class="peer z-20 placeholder:text-transparent {dirProjectIdError === '' ? '' : 'border-red-600'}"/>
										<label for="dir-project-id" class="absolute text-gray-500 font-normal duration-200 transform -translate-y-4 scale-[.65] top-3 z-10 origin-[0] left-2.5 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:font-light peer-focus:scale-[.65] peer-focus:-translate-y-4">DIR Project ID</label>
									</div>
									<p class="error-text {dirProjectIdError === '' ? 'hidden' : ''}">{dirProjectIdError}</p>
								</div>
								<div class={colCls}>
									<div class="relative">
										<input id="awarding-body" name="awarding_body" bind:value={awardingBody} placeholder="Awarding Body" class="peer z-20 placeholder:text-transparent {awardingBodyError === '' ? '' : 'border-red-600'}"/>
										<label for="awarding-body" class="absolute text-gray-500 font-normal duration-200 transform -translate-y-4 scale-[.65] top-3 z-10 origin-[0] left-2.5 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:font-light peer-focus:scale-[.65] peer-focus:-translate-y-4">Awarding Body</label>
									</div>
									<p class='error-text {awardingBodyError === '' ? 'hidden' : ''}'>{awardingBodyError}</p>
								</div>
							</div>

							<div class={rowCls}>
								<div class={colCls}>
									<div class="relative">
										<input id="contractor" name="contractor" bind:value={contractor} placeholder="Contractor Name" class="peer z-20 placeholder:text-transparent  {contractorError === '' ? '' : 'border-red-600'}"/>
										<label for="contractor" class="absolute text-gray-500 font-normal duration-200 transform -translate-y-4 scale-[.65] top-3 z-10 origin-[0] left-2.5 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:font-light peer-focus:scale-[.65] peer-focus:-translate-y-4">Contractor (% for fuzzy match)</label>
									</div>
									<p class='error-text {contractorError === '' ? 'hidden' : ''}'>{contractorError}</p>
								</div>
								<div class={colCls}>
									<div class="relative">
										<input id="pwcr-number" name="pwcr_num" bind:value={pwcrNumber} placeholder="PWCR Number" class="peer z-20 placeholder:text-transparent  {pwcrNumberError === '' ? '' : 'border-red-600'}"/>
										<label for="pwcr-number" class="absolute text-gray-500 font-normal duration-200 transform -translate-y-4 scale-[.65] top-3 z-10 origin-[0] left-2.5 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:font-light peer-focus:scale-[.65] peer-focus:-translate-y-4">PWCR Number</label>
									</div>
									<p class="error-text {pwcrNumberError === '' ? 'hidden' : ''}">{pwcrNumberError}</p>
								</div>
							</div>

							<div class={rowCls}>
								<div class={colCls}>
									<div class="relative">
										<input id="project-name" name="proj_name" bind:value={projectName} placeholder="Project Name" class="peer z-20 placeholder:text-transparent  {projectNameError === '' ? '' : 'border-red-600'}"/>
										<label for="project-name" class="absolute text-gray-500 font-normal duration-200 transform -translate-y-4 scale-[.65] top-3 z-10 origin-[0] left-2.5 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:font-light peer-focus:scale-[.65] peer-focus:-translate-y-4">Project Name</label>
									</div>
									<p class='error-text {projectNameError === '' ? 'hidden' : ''}'>{projectNameError}</p>
								</div>
								<div class={colCls}>
									<div class="relative">
										<select id="county" name="county" bind:value={county} class="input-field peer mt-4 {countyError === '' ? '' : 'border-red-600'} w-full">
											<option value="">Select a County</option><option value="Out of state:">Out of state:</option><option value="Alameda">Alameda</option><option value="Alpine">Alpine</option><option value="Amador">Amador</option><option value="Butte">Butte</option><option value="Calaveras">Calaveras</option><option value="Colusa">Colusa</option><option value="Contra Costa">Contra Costa</option><option value="Del Norte">Del Norte</option><option value="El Dorado">El Dorado</option><option value="Fresno">Fresno</option><option value="Glenn">Glenn</option><option value="Humboldt">Humboldt</option><option value="Imperial">Imperial</option><option value="Inyo">Inyo</option><option value="Kern">Kern</option><option value="Kings">Kings</option><option value="Lake">Lake</option><option value="Lassen">Lassen</option><option value="Los Angeles">Los Angeles</option><option value="Madera">Madera</option><option value="Marin">Marin</option><option value="Mariposa">Mariposa</option><option value="Mendocino">Mendocino</option><option value="Merced">Merced</option><option value="Modoc">Modoc</option><option value="Mono">Mono</option><option value="Monterey">Monterey</option><option value="Napa">Napa</option><option value="Nevada">Nevada</option><option value="Orange">Orange</option><option value="Placer">Placer</option><option value="Plumas">Plumas</option><option value="Riverside">Riverside</option><option value="Sacramento">Sacramento</option><option value="San Benito">San Benito</option><option value="San Bernardino">San Bernardino</option><option value="San Diego">San Diego</option><option value="San Francisco">San Francisco</option><option value="San Joaquin">San Joaquin</option><option value="San Luis Obispo">San Luis Obispo</option><option value="San Mateo">San Mateo</option><option value="Santa Barbara">Santa Barbara</option><option value="Santa Clara">Santa Clara</option><option value="Santa Cruz">Santa Cruz</option><option value="Shasta">Shasta</option><option value="Sierra">Sierra</option><option value="Siskiyou">Siskiyou</option><option value="Solano">Solano</option><option value="Sonoma">Sonoma</option><option value="Stanislaus">Stanislaus</option><option value="Sutter">Sutter</option><option value="Tehama">Tehama</option><option value="Trinity">Trinity</option><option value="Tulare">Tulare</option><option value="Tuolumne">Tuolumne</option><option value="Ventura">Ventura</option><option value="Yolo">Yolo</option><option value="Yuba">Yuba</option>
										</select>
										<label for="county" class="absolute text-gray-500 font-normal duration-200 transform -translate-y-5 scale-[.65] top-3 z-10 origin-[0] left-2.5 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:font-light peer-focus:scale-[.65]">County</label>
									</div>
									<p class='error-text {countyError === '' ? 'hidden' : ''}'>{countyError}</p>
								</div>
							</div>

							{#if eggMessage}
								<div class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
									<div class="bg-white rounded-lg p-8 max-w-lg mx-4 shadow-xl">
										<p class="text-xl text-blue-900 mb-6">{eggMessage}</p>
										<div class="flex justify-end">
											<button type="button" on:click={() => eggMessage = ''}
												class="px-4 py-2 rounded-md bg-blue-500 hover:bg-blue-700 text-white font-semibold">
												Understood
											</button>
										</div>
									</div>
								</div>
							{/if}

							<div class={rowCls}>
								<div class={colCls}>
									<div class="relative">
										<input class="bc-sliver" type="text" maxlength="1" value={bcStart} aria-hidden="true" tabindex="-1"
											on:input={(e) => bcStart = sliverInput(e)}/>
										<input id="start-date" value={startDate} placeholder="Start Date" type='date'
											min={MIN_DATE} max={MAX_DATE} class="{startDateError === '' ? '' : 'border-red-600'}"
											on:change={(e) => guardField(e, 'cpr-start')}
											/>
									</div>
									<p class='error-text {startDateError === '' ? 'hidden' : ''}'>{startDateError}</p>
								</div>
								<div class={colCls}>
									<div class="relative">
										<input class="bc-sliver" type="text" maxlength="1" value={bcEnd} aria-hidden="true" tabindex="-1"
											on:input={(e) => bcEnd = sliverInput(e)}/>
										<input id="end-date" value={endDate} placeholder="End Date" type='date'
											min={MIN_DATE} max={MAX_DATE} class="{endDateError === '' ? '' : 'border-red-600'}"
											on:change={(e) => guardField(e, 'cpr-end')}
											/>
									</div>
									<p class='error-text {endDateError === '' ? 'hidden' : ''}'>{endDateError}</p>
								</div>
							</div>

							<input type="hidden" name="start_date" value={(bcStart === '-' && startDate ? '-' : '') + startDate}/>
							<input type="hidden" name="end_date" value={(bcEnd === '-' && endDate ? '-' : '') + endDate}/>

							<div class={rowCls}>
								<!-- Sort by removed — appears broken, can restore if needed
								<div class='input {splitView ? "w-full" : "w-1/3"}'>
									<div class="relative">
										<select id="sort_by" name="sort_by" bind:value={sortByInput} class="input-field w-full mt-4 {sortByError === '' ? '' : 'border-red-600'}">
											<option value="">Select a Sort Filter</option><option value="DIR Project ID">DIR Project ID</option><option value="Awarding Body">Awarding Body</option><option value="Contractor Name">Contractor Name</option><option value="Project Name">Project Name</option><option value="Date">Date</option><option value="Contractor PWCR">Contractor PWCR</option><option value="County">County</option><option value="CSLB License">CSLB License</option>
										</select>
										<label for="sort_by" class="absolute text-gray-500 font-normal duration-200 transform -translate-y-5 scale-[.65] top-3 z-10 origin-[0] left-2.5 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:font-light peer-focus:scale-[.65]">Sort By</label>
									</div>
									<p class='error-text {sortByError === '' ? 'hidden' : ''}'>{sortByError}</p>
								</div>
								-->
							

								<div class="input inline-block w-Full">
									<div class="tooltip-div">
										<legend class="my-auto pb-2 w-Full">Premium Options: </legend>
										<p class='tooltip'>Please contact us for premium license, support@paloaltodatagroup.com</p>
									</div>

									<div class="flex my-auto text-lg space-x-4 {limitBypassError === '' ? '' : 'border-red-600'}">
										<input id="limit-bypass" name="limit_bypass" bind:checked={limitBypass} disabled={!limitBypassFeature} type="checkbox" class="w-5 ml-4"/>
										<label for="limit-bypass">250+ Limit Bypass</label>
										<p class='error-text {limitBypassError === '' ? 'hidden' : ''}'>{limitBypassError}</p>
									</div>

									<div class="tooltip-div">
										<div class="flex my-auto text-lg space-x-4 {scrapePDFsError === '' ? '' : 'border-red-600'}">
											<input id="scrape-pdfs" name="scrape_pdfs" bind:checked={scrapePDFs} disabled={!scrapePDFsFeature} type="checkbox" class="w-5 ml-4"/>
											<label for="scrape-pdfs">CSV Report + Contractor Tree</label>
											<p class='error-text {scrapePDFsError === '' ? 'hidden' : ''}'>{scrapePDFsError}</p>
										</div>
										<p class='tooltip'>Downloads selected records as a spreadsheet csv report, including the precedence of subcontractors.</p>
									</div>
									<input type="checkbox" name="generate_contractor_tree" checked={generateContractorTree} class="hidden"/>

									<div class="flex my-auto text-lg space-x-4 {sepNPError === '' ? '' : 'border-red-600'}">
										<input id="separate-np" name="sep_np" bind:checked={sepNP} disabled={!sepNPFeature} type="checkbox" class="w-5 ml-4"/>
										<label for="separate-np">Separate Non-Performance Statements</label>
										<p class='error-text {sepNPError === '' ? 'hidden' : ''}'>{sepNPError}</p>
									</div>

									<div class="tooltip-div">
										<div class="flex my-auto text-lg space-x-4 items-center mt-2">
											<button type="button" on:click={openDirLogin}
												class="ml-4 px-3 py-1 text-base rounded-md border font-semibold flex items-center gap-1.5 {dirCredConfigured ? 'border-green-600 text-green-700 bg-green-50 hover:bg-green-100' : 'border-blue-500 text-blue-600 hover:bg-blue-50'}">
												{#if dirCredConfigured}
													<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 9.9-1"></path></svg>
													DIR Unlocked
												{:else}
													<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
													DIR Login
												{/if}
											</button>
											<span class="text-sm {dirCredConfigured ? 'text-green-700 font-medium' : 'text-gray-600'}">
												{dirCredConfigured ? `Unthrottled — using ${dirCredUsername}` : 'Public portal (throttled)'}
											</span>
										</div>
										<p class='tooltip'>Use your own DIR account for unthrottled downloads. Without credentials, searches use the public portal rate limit.</p>
									</div>
								</div>
									
								<div class="input inline-block w-Full">

									<!--<legend class="my-auto pb-2 w-Full">Databases:</legend>-->

									{#if hiddenFeature}
									<div class="tooltip-div">
										<div class="flex my-auto text-lg space-x-4 {aiFeature ? '' : 'hover:cursor-not-allowed'} {aiError === '' ? '' : 'border-red-600'}">
											<input id="ai-PR" name="ai_PR" bind:checked={aiPR} disabled={!aiFeature} type="checkbox" class="w-5 ml-4"/>
											<label for="ai-PR" class="{aiFeature  ? '' : 'text-gray-500'}">ai.PR Search (in development)</label>
											<p class='error-text {aiError === '' ? 'hidden' : ''}'>{aiError}</p>
										</div>
										<p class='tooltip'>Search ai-simulated payroll records.</p>
									</div>
									<div class="tooltip-div">
										<div class="flex my-auto text-lg space-x-4 {sensoryFeature ? '' : 'hover:cursor-not-allowed'} {sensoryError === '' ? '' : 'border-red-600'}">
											<input id="sensory-PR" name="sensory_PR" bind:checked={sensoryPR} disabled={!sensoryFeature} type="checkbox" class="w-5 ml-4"/>
											<label for="sensory-PR" class="{sensoryFeature ? '' : 'text-gray-500'}">sensory.PR Search (in development)</label>
											<p class='error-text {sensoryError === '' ? 'hidden' : ''}'>{sensoryError}</p>
										</div>
										<p class='tooltip'>Search sensory-based reconstruction of payroll records.</p>
									</div>
									{/if}

								</div>
							</div>

							<div class="flex space-x-4">
								<button type="submit" disabled={previewSubmit}
									class="{splitView ? 'w-full' : 'w-4/12'} pl-0 pb-2 pt-1 text-xl rounded-md bg-blue-500 disabled:bg-blue-500 disabled:text-white hover:bg-blue-700 cursor-pointer disabled:cursor-wait text-white font-semibold border-none shadow-sm">
									Submit
								</button>
							</div>

							<div class="flex space-x-4">
								
								{#if hiddenFeature}
								<!--always on-->
								<div class="flex my-auto text-lg space-x-4 {useV2ParserError === '' ? '' : 'border-red-600'}">
									<input id="use-v2-parser" name="use_v2_parser" bind:checked={useV2Parser} type="checkbox" class="w-5"/>
									<label for="use-v2-parser">v2 Parser</label>
									<p class='error-text {useV2ParserError === '' ? 'hidden' : ''}'>{useV2ParserError}</p>
								</div>
							
								<div class="flex my-auto text-lg space-x-4 {includePDFsError === '' ? '' : 'border-red-600'}">
									<input id="include-pdfs" name="include_pdfs" bind:checked={includePDFs} type="checkbox" class="w-5"/>
									<label for="include-pdfs">Include PDFs</label>
									<p class='error-text {includePDFsError === '' ? 'hidden' : ''}'>{includePDFsError}</p>
								</div>
								{/if}
								
							</div>

						</div>
					</form>
				</div>
				{#if splitView}
					<div
						role="separator"
						aria-orientation="vertical"
						class="w-1.5 shrink-0 self-stretch cursor-col-resize rounded bg-gray-300 hover:bg-blue-400 touch-none select-none"
						on:pointerdown|preventDefault={dividerDown}
						on:pointermove={dividerMove}
						on:pointerup={dividerUp}
						on:pointercancel={dividerUp}
					></div>
				{/if}
				<div class="{searchState === 'preview-data' ? (panelOpen ? 'min-w-0 flex-1 pl-3' : '') : 'hidden'}">
					<div class="flex items-center justify-between">
						<p class="text-2xl font-semibold text-blue-900">Preview CPR Documents</p>
						<button type="button" on:click={togglePanel}
							class="px-3 py-1 text-base rounded-md border border-blue-500 text-blue-600 hover:bg-blue-50 font-semibold"
							title="{panelOpen ? 'Collapse search panel' : 'Edit search alongside results'}">
							{panelOpen ? '⟩ Hide Search' : '⟨ Search'}
						</button>
					</div>
					<p class="text-lg font-semibold text-blue-900 mb-4">Double-click a row to preview PDF.</p>
					<p class="text-red-500 {otherError === '' ? '' : 'text-xl pb-5'}">{otherError}</p>
					<p class="text-blue-600 {previewDataNotice === '' ? 'hidden' : 'text-lg pb-2'}">{previewDataNotice}</p>
					<p class="text-lg mb-3">
						{totalPreviewResults.toLocaleString()} {totalPreviewResults == 1 ? "match" : "matches"} found
						{#if previewData.length !== displayedPreviewData.length}
							({previewData.length - displayedPreviewData.length} non-performance hidden)
						{/if}
					</p>
					{#if previewData.length < totalPreviewResults}
						<p class="text-orange-600 mb-4">
							Showing first {previewData.length} results in preview. Download full results for complete data.
						</p>
					{/if}
					{#if filteredPreviewData.length > 0 && filteredPreviewData.length < displayedPreviewData.length}
						<p class="text-lg mb-3 -mt-2">showing {filteredPreviewData.length} of {displayedPreviewData.length} (filters active) &mdash; <button type="button" class="text-blue-600 underline" on:click={() => tableFilterSelections = {}}>clear</button></p>
					{/if}
					<div class="mb-3 flex items-center">
						<input type="checkbox" id="hide-nps" class="w-4 h-4 mr-2" bind:checked={hideNPS} />
						<label for="hide-nps">Hide Non-Performance Statements</label>
					</div>
					<div class="overflow-auto max-h-[65vh]">
						<SvelteTable
							on:clickCell={(event) => {
								if (event.detail.key === 'select') {
									toggleSelected(event.detail.row.id);
								}
							}}
							bind:filteredRows={filteredPreviewData}
							bind:filterSelections={tableFilterSelections}
							columns={desktopCols}
							rows={displayedPreviewData}
							classNameRow={'cursor-pointer odd:bg-white even:bg-gray-200'}
							classNameThead={['table-primary sticky top-0 bg-white']}
							classNameTbody={['w-full overflow-scroll']}
							classNameSelect={['rounded-md shadow-sm font-thin py-1 px-2']}
							on:clickRow={(event) => {
								if (event.detail.event.detail < 2) return; // only open on double-click
								const row = event.detail.row;
								if (row.fileId) {
								window.open(`/api/v1/b2/download/${row.fileId}.pdf`, '_blank', 'noopener');
								} else {
								alert('Submit Search to download this file.');
								}
							}}
						/>
					</div>

					{#if totalPreviewResults > 8000}
						<p class="text-red-600 font-bold text-lg">Warning: This search matched {totalPreviewResults.toLocaleString()} records and may take a very long time. Consider narrowing your date range or adding filters.</p>
					{/if}
					{#if feeLiable}
						<p class="mt-4 text-lg text-blue-900 font-semibold text-center">
							Download fee: up to ${approvedFee} ({approvedFileCount.toLocaleString()} files &times; $0.10)
						</p>
						<p class="text-sm text-gray-600 text-center">
							Billed to your card on the monthly invoice. You pay only for files delivered — never more than this maximum.
						</p>
					{/if}
					<div class="mt-6 space-x-5 flex justify-center">
						<div class="relative inline-block">
							<div class="flex">
								<button on:click={() => { useOCR = false; handleSearch(); }} disabled={searchSubmit || (!scrapePDFsFeature && !feeLiable)}
									class="px-6 py-2 text-xl rounded-l-md {(scrapePDFsFeature || feeLiable) ? 'bg-blue-500 hover:bg-blue-700 cursor-pointer' : 'bg-gray-400 cursor-not-allowed'} disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold"
									title={(scrapePDFsFeature || feeLiable) ? '' : 'CSV Report feature required'}>
									{feeLiable ? 'Accept Fee & Download' : 'Download Records'}
								</button>
								<button type="button" disabled={searchSubmit || !scrapePDFsFeature}
									on:click={() => showSubmitDropdown = !showSubmitDropdown}
									class="px-2 py-2 text-xl rounded-r-md {scrapePDFsFeature ? 'bg-blue-600 hover:bg-blue-800 cursor-pointer' : 'bg-gray-400 cursor-not-allowed'} disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold">
									&#9660;
								</button>
							</div>
							{#if showSubmitDropdown}
								<div class="absolute right-0 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg z-50">
									<button type="button" class="w-full text-left px-4 py-2 text-blue-700 hover:bg-gray-100 text-base font-medium"
										on:click={() => { useOCR = true; showSubmitDropdown = false; handleSearch(); }}>
										OCR Deep Extract
									</button>
								</div>
							{/if}
						</div>
						{#if !splitView}
							<button type="button" on:click={newSearch} class="px-6 py-2 text-xl rounded-md bg-blue-500 hover:bg-blue-700 cursor-pointer text-white font-semibold">
								New Search
							</button>
						{/if}
					</div>
					<!-- Mirrors the selection-passback logic in handleSearch(): checked rows win;
					     else active dropdown filter; else no restriction = all server-side matches. -->
					<p class="mt-2 text-sm text-gray-600 text-center">
						{#if selectedIds.size > 0}
							Downloads {selectedIds.size} selected {selectedIds.size == 1 ? "record" : "records"}
						{:else if filteredPreviewData.length > 0 && filteredPreviewData.length < displayedPreviewData.length}
							Downloads {filteredPreviewData.length} filtered results
						{:else}
							Downloads all {totalPreviewResults.toLocaleString()} matching records
						{/if}
					</p>
				</div>
				</div> <!-- end split-view wrapper -->

				<div class="{searchState === 'aggregating-results' ? '' : 'hidden'}">
					<p class="text-2xl font-semibold text-blue-900">{searchStatus === 'finished' ? 'Results Ready to Download' : 'Aggregating Results: See History to Download'}</p>
					<p class="text-red-500 {otherError === '' ? '' : 'text-xl pb-5'}">{otherError}</p>
					{#if bundleNotice}
						<div class="bg-amber-50 border-l-4 border-amber-400 p-3 mb-3 text-sm text-amber-900">
							<p><strong>{bundleNotice.includes('Bundle mode canceled') ? 'Bundle download canceled.' : 'Bundle download active.'}</strong> {@html boldNoticeTimes(bundleNotice)}</p>
							<p class="mt-1">Your download will contain approximately 1/20th the number of files — each bundle file combines ~20 records into one PDF.</p>
						</div>
					{/if}
					{#if statusWarning && statusWarning !== bundleNotice}
						<p class="text-amber-600 text-lg pb-3">{statusWarning}</p>
					{/if}
					<div class="mt-5 space-y-3">
						<p class="text-xl font-semibold">Search ID: {searchId}</p>
						<p class="text-xl font-semibold">Status: {capitalizeFirstLetter(searchStatus)}</p>
						{#if searchStatus === 'downloading'}
							{#if typeof totalFiles === 'number' && totalFiles > 0}
								<p class="text-xl font-semibold">Files downloaded: {downloadedFiles} from {totalFiles} records</p>
							{:else if dirScopeTotal > 0}
								<p class="text-xl font-semibold">Files downloaded: {downloadedFiles} of {dirScopeTotal.toLocaleString()}</p>
							{:else if estimatedTotalFiles > 0}
								<p class="text-xl font-semibold">Files downloaded: {downloadedFiles} of ~{estimatedTotalFiles.toLocaleString()} (estimate)</p>
							{:else}
								<p class="text-xl font-semibold">Files downloaded: {downloadedFiles} &mdash; total being determined</p>
							{/if}
							<p class="text-lg text-gray-600 italic">{blurbText}</p>
							{#if downloadedFiles > 2 && totalFiles > 0}
							<p class="text-xl text-gray-700"><strong>~{Math.ceil(((Date.now() - downloadStartTime) / 60000 / downloadedFiles) * (totalFiles - downloadedFiles))} min remaining</strong></p>
							<p class="text-sm text-gray-500 mt-1">Runs at approximately 2× speed outside California business hours (before 6 AM / after 6 PM, and weekends).</p>
						{/if}
					{:else if searchStatus === 'finished'}
					{#if feeLiable && acceptedFileCount > 0}
						<p class="mt-3 text-lg text-blue-900 font-semibold">
							Fee: ${(Math.min(deliveredFileCount, acceptedFileCount) * PER_FILE_FEE).toFixed(2)}
							for {Math.min(deliveredFileCount, acceptedFileCount).toLocaleString()} files delivered
							(accepted maximum: ${(acceptedFileCount * PER_FILE_FEE).toFixed(2)})
						</p>
					{/if}
					<div class="space-x-5 mt-5">
							{#if scrapePDFsFeature || feeLiable}
								<a href="{getGETAPILink('ecpr-search/download', {'search_id': searchId})}" download="{searchId}.zip" target="_self">
								<button class="w-2/12 pl-0 pb-2 pt-2 text-xl rounded-md bg-blue-500 hover:bg-blue-700 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
								Download</button></a>
							{:else}
								<button disabled class="w-2/12 pl-0 pb-2 pt-2 text-xl rounded-md bg-gray-400 cursor-not-allowed text-gray-200 font-semibold border-none shadow-sm"
									title="CSV Report feature required for download">
								Download</button>
							{/if}
							<button on:click={newSearch}
								class="w-2/12 pl-0 pb-2 pt-2 text-xl rounded-md bg-blue-500 hover:bg-blue-700 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
								New Search
							</button>
						</div>
						{/if}
						{#if (searchStatus !== 'finished') && (searchStatus !== 'error')}
							<div class="inline-block w-full mt-5">
								<button on:click={() => cancelSearch(searchId)} disabled={cancelSubmit}
									class="w-2/12 pl-0 pb-2 pt-1 text-xl rounded-md bg-blue-500 hover:bg-blue-700 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
								Cancel</button>
							</div>
						{:else if searchStatus === 'error'}
							<div class="inline-block w-full mt-5">
								<button on:click={newSearch}
									class="w-2/12 pl-0 pb-2 pt-2 text-xl rounded-md bg-blue-500 hover:bg-blue-700 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
								New Search</button>
							</div>
						{/if}
					</div>
				</div>

			</div>
			
			<!--MOBILE-->
			<div class="block lg:hidden"> 
				<div class="{searchState === 'preview-search' ? '' : 'hidden'}">
					<p class="text-2xl font-semibold text-blue-900 mb-0">Search Payroll Records</p>
					<p class="text-red-500 {otherError === '' ? '' : 'text-xl pb-5'}">{otherError}</p>

					<!-- DATABASE SELECTION - Top of Mobile Form -->
					<div class="database-selector-container">
						<p class="text-lg font-semibold text-gray-700 mb-3">Select Database(s):</p>
						<div class="database-icons-wrapper">
							<!-- Public Database Cylinder -->
							<button 
								type="button"
								class="database-cylinder {localSearch ? 'active' : ''}"
								on:click={() => localSearch = !localSearch}
								disabled={!localSearchFeature}
							>
								<div class="cylinder-top"></div>
								<div class="cylinder-body">
									<svg class="database-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
										<ellipse cx="12" cy="5" rx="9" ry="3"></ellipse>
										<path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"></path>
										<path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path>
									</svg>
								</div>
								<div class="cylinder-label">
									<span class="db-title">Public db</span>
									<span class="db-subtitle">DLSE eCPR</span>
								</div>
								{#if localSearch}
									<div class="selection-badge">✓</div>
								{/if}
							</button>

							<!-- Private Database Cylinder -->
							<button 
								type="button"
								class="database-cylinder {privateCPR ? 'active' : ''}"
								on:click={() => privateCPR = !privateCPR}
								disabled={!privateCPRFeature}
							>
								<div class="cylinder-top"></div>
								<div class="cylinder-body">
									<svg class="database-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
										<ellipse cx="12" cy="5" rx="9" ry="3"></ellipse>
										<path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"></path>
										<path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path>
										<rect x="9" y="9" width="6" height="7" rx="1"></rect>
										<path d="M12 13v2"></path>
									</svg>
								</div>
								<div class="cylinder-label">
									<span class="db-title">Private db</span>
									<span class="db-subtitle">Your Uploads</span>
								</div>
								{#if privateCPR}
									<div class="selection-badge">✓</div>
								{/if}
							</button>
						</div>
						<p class="text-sm text-gray-500 mt-2 text-center">Click database icons to select/deselect</p>
					</div>

					<form method="POST" class="h-full relative" bind:this={searchForm} on:submit|preventDefault={handlePreview}>
						<input type="hidden" id="selected-file-ids-mobile" name="selected_file_ids" value="">
						<input type="hidden" id="selected-pids-mobile" name="selected_pids" value="">
						<input type="hidden" id="approved-file-count-mobile" name="approved_file_count" value="">
						<!-- <input id="csrf_token" name="csrf_token" value="" class="hidden"> -->

						<!-- Hidden inputs for form submission -->
						<input type="checkbox" id="local-search-mobile" name="local_search" value="on" class="hidden" />
						<input type="checkbox" id="priv-db-search-mobile" name="priv_db_search" value="on" class="hidden" />
						<input type="checkbox" id="use-ocr-mobile" name="use_ocr" value="on" class="hidden" bind:checked={useOCR} />

						<div class="flex flex-col space-y-3 text-xl">
							<div class='input'>
								<div class="relative">
									<input id="dir-project-id-mobile" name="proj_id" bind:value={dirProjectId} placeholder="DIR Project ID" class="peer z-20 placeholder:text-transparent {dirProjectIdError === '' ? '' : 'border-red-600'}"/>
									<label for="dir-project-id-mobile" class="absolute text-gray-500 font-normal duration-200 transform -translate-y-4 scale-[.65] top-3 z-10 origin-[0] left-2.5 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:font-light peer-focus:scale-[.65] peer-focus:-translate-y-4">DIR Project ID (optional)</label>
								</div>
								<p class="error-text {dirProjectIdError === '' ? 'hidden' : ''}">{dirProjectIdError}</p>
							</div>
							<div class='input'>
								<div class="relative">
									<input id="awarding-body-mobile" name="awarding_body" bind:value={awardingBody} placeholder="Awarding Body" class="peer z-20 placeholder:text-transparent {awardingBodyError === '' ? '' : 'border-red-600'}"/>
									<label for="awarding-body-mobile" class="absolute text-gray-500 font-normal duration-200 transform -translate-y-4 scale-[.65] top-3 z-10 origin-[0] left-2.5 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:font-light peer-focus:scale-[.65] peer-focus:-translate-y-4">Awarding Body (optional)</label>
								</div>
								<p class='error-text {awardingBodyError === '' ? 'hidden' : ''}'>{awardingBodyError}</p>
							</div>
							<div class='input'>
								<div class="relative">
									<input id="project-name-mobile" name="proj_name" bind:value={projectName} placeholder="Project Name" class="peer z-20 placeholder:text-transparent  {projectNameError === '' ? '' : 'border-red-600'}"/>
									<label for="project-name-mobile" class="absolute text-gray-500 font-normal duration-200 transform -translate-y-4 scale-[.65] top-3 z-10 origin-[0] left-2.5 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:font-light peer-focus:scale-[.65] peer-focus:-translate-y-4">Project Name (optional)</label>
								</div>
								<p class='error-text {projectNameError === '' ? 'hidden' : ''}'>{projectNameError}</p>
							</div>
							<div class='input'>
								<div class="relative">
									<input id="contractor-mobile" name="contractor" bind:value={contractor} placeholder="Contractor Name" class="peer z-20 placeholder:text-transparent  {contractorError === '' ? '' : 'border-red-600'}"/>
									<label for="contractor-mobile" class="absolute text-gray-500 font-normal duration-200 transform -translate-y-4 scale-[.65] top-3 z-10 origin-[0] left-2.5 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:font-light peer-focus:scale-[.65] peer-focus:-translate-y-4">Contractor (optional)</label>
								</div>
								<p class='error-text {contractorError === '' ? 'hidden' : ''}'>{contractorError}</p>
							</div>
							<div class='input'>
								<div class="relative">
									<input id="pwcr-number-mobile" name="pwcr_num" bind:value={pwcrNumber} placeholder="PWCR Number" class="peer z-20 placeholder:text-transparent  {pwcrNumberError === '' ? '' : 'border-red-600'}"/>
									<label for="pwcr-number-mobile" class="absolute text-gray-500 font-normal duration-200 transform -translate-y-4 scale-[.65] top-3 z-10 origin-[0] left-2.5 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:font-light peer-focus:scale-[.65] peer-focus:-translate-y-4">PWCR Number (optional)</label>
								</div>
								<p class="error-text {pwcrNumberError === '' ? 'hidden' : ''}">{pwcrNumberError}</p>
							</div>

							<fieldset class="flex-col space-y-2">
								<div class="flex space-x-2">
									<div class='input w-1/2'>
										<DateTypeInput id="start-date-mobile" name="start_date" label="Start Date" value={startDate} error={startDateError}
											on:change={(e) => { startDateError = ''; guardValue(e.detail, 'cpr-start'); }}
											on:invalid={() => startDateError = 'Enter date as MM/DD/YYYY'}/>
									</div>
									<p class="my-auto">-</p> 
									<div class='input w-1/2'>
										<DateTypeInput id="end-date-mobile" name="end_date" label="End Date" value={endDate} error={endDateError}
											on:change={(e) => { endDateError = ''; guardValue(e.detail, 'cpr-end'); }}
											on:invalid={() => endDateError = 'Enter date as MM/DD/YYYY'}/>
									</div>
								</div>
							</fieldset>

							<!--BUTTON-->
							<button type="submit" disabled={previewSubmit}
								class="w-5/12 pl-0 pb-2 pt-1 text-xl rounded-md bg-blue-500 disabled:bg-blue-500 disabled:text-white hover:bg-blue-700 cursor-pointer disabled:cursor-wait text-white font-semibold border-none shadow-sm">
								Submit
							</button>
							
							<!--OTHER FILTERs-->
							<div class='input'>
								<div class="relative">
									<select id="county-mobile" name="county" bind:value={county} class="input-field peer mt-4 {countyError === '' ? '' : 'border-red-600'} w-full">
										<option value="">Select a County (optional)</option><option value="Out of state:">Out of state:</option><option value="Alameda">Alameda</option><option value="Alpine">Alpine</option><option value="Amador">Amador</option><option value="Butte">Butte</option><option value="Calaveras">Calaveras</option><option value="Colusa">Colusa</option><option value="Contra Costa">Contra Costa</option><option value="Del Norte">Del Norte</option><option value="El Dorado">El Dorado</option><option value="Fresno">Fresno</option><option value="Glenn">Glenn</option><option value="Humboldt">Humboldt</option><option value="Imperial">Imperial</option><option value="Inyo">Inyo</option><option value="Kern">Kern</option><option value="Kings">Kings</option><option value="Lake">Lake</option><option value="Lassen">Lassen</option><option value="Los Angeles">Los Angeles</option><option value="Madera">Madera</option><option value="Marin">Marin</option><option value="Mariposa">Mariposa</option><option value="Mendocino">Mendocino</option><option value="Merced">Merced</option><option value="Modoc">Modoc</option><option value="Mono">Mono</option><option value="Monterey">Monterey</option><option value="Napa">Napa</option><option value="Nevada">Nevada</option><option value="Orange">Orange</option><option value="Placer">Placer</option><option value="Plumas">Plumas</option><option value="Riverside">Riverside</option><option value="Sacramento">Sacramento</option><option value="San Benito">San Benito</option><option value="San Bernardino">San Bernardino</option><option value="San Diego">San Diego</option><option value="San Francisco">San Francisco</option><option value="San Joaquin">San Joaquin</option><option value="San Luis Obispo">San Luis Obispo</option><option value="San Mateo">San Mateo</option><option value="Santa Barbara">Santa Barbara</option><option value="Santa Clara">Santa Clara</option><option value="Santa Cruz">Santa Cruz</option><option value="Shasta">Shasta</option><option value="Sierra">Sierra</option><option value="Siskiyou">Siskiyou</option><option value="Solano">Solano</option><option value="Sonoma">Sonoma</option><option value="Stanislaus">Stanislaus</option><option value="Sutter">Sutter</option><option value="Tehama">Tehama</option><option value="Trinity">Trinity</option><option value="Tulare">Tulare</option><option value="Tuolumne">Tuolumne</option><option value="Ventura">Ventura</option><option value="Yolo">Yolo</option><option value="Yuba">Yuba</option>
									</select>
									<label for="county-mobile" class="absolute text-gray-500 font-normal duration-200 transform -translate-y-5 scale-[.65] top-3 z-10 origin-[0] left-2.5 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:font-light peer-focus:scale-[.65]">County</label>
								</div>
								<p class='error-text {countyError === '' ? 'hidden' : ''}'>{countyError}</p>
							</div>
							<!-- Sort by removed — appears broken, can restore if needed
							<div class='input'>
								<div class="relative">
									<select id="sort_by-mobile" name="sort_by" bind:value={sortByInput} class="input-field w-full mt-4 {sortByError === '' ? '' : 'border-red-600'}">
										<option value="">Select a Sort Filter</option><option value="DIR Project ID">DIR Project ID</option><option value="Awarding Body">Awarding Body</option><option value="Contractor Name">Contractor Name</option><option value="Project Name">Project Name</option><option value="Date">Date</option><option value="Contractor PWCR">Contractor PWCR</option><option value="County">County</option><option value="CSLB License">CSLB License</option>
									</select>
									<label for="sort_by-mobile" class="absolute text-gray-500 font-normal duration-200 transform -translate-y-5 scale-[.65] top-3 z-10 origin-[0] left-2.5 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:font-light peer-focus:scale-[.65]">Sort By</label>
								</div>
								<p class='error-text {sortByError === '' ? 'hidden' : ''}'>{sortByError}</p>
							</div>
							-->

							<!--PREMIUM OPTIONS-->
							
							<!--<div class="input inline-block w-Full">-->
							<div class="tooltip-div">
								<legend class="my-auto pb-2 w-Full">Premium Options:</legend>
								<p class='tooltip'>For premium license: support@paloaltodatagroup.com</p>
							</div>

							<div class="flex my-auto text-lg space-x-4 {limitBypassError === '' ? '' : 'border-red-600'}">
								<input id="limit-bypass-mobile" name="limit_bypass" bind:checked={limitBypass} disabled={!limitBypassFeature} type="checkbox" class="w-5"/>
								<label for="limit-bypass-mobile">250+ Limit Bypass</label>
								<p class='error-text {limitBypassError === '' ? 'hidden' : ''}'>{limitBypassError}</p>
							</div>

							<div class="flex my-auto text-lg space-x-4 {scrapePDFsError === '' ? '' : 'border-red-600'}">
								<input id="scrape-pdfs-mobile" name="scrape_pdfs" bind:checked={scrapePDFs} disabled={!scrapePDFsFeature} type="checkbox" class="w-5"/>
								<label for="scrape-pdfs-mobile">CSV Report + Contractor Tree</label>
								<p class='error-text {scrapePDFsError === '' ? 'hidden' : ''}'>{scrapePDFsError}</p>
							</div>
							<input type="checkbox" name="generate_contractor_tree" checked={generateContractorTree} class="hidden"/>

							<div class="flex my-auto text-lg space-x-4 {sepNPError === '' ? '' : 'border-red-600'}">
								<input id="separate-np-mobile" name="sep_np" bind:checked={sepNP} disabled={!sepNPFeature} type="checkbox" class="w-6"/>
								<label for="separate-np-mobile">Separate Non-Performance Statements</label>
								<p class='error-text {sepNPError === '' ? 'hidden' : ''}'>{sepNPError}</p>
							</div>

							<div class="flex my-auto text-base space-x-3 items-center">
								<button type="button" on:click={openDirLogin}
									class="px-3 py-1 rounded-md border font-semibold flex items-center gap-1.5 {dirCredConfigured ? 'border-green-600 text-green-700 bg-green-50 hover:bg-green-100' : 'border-blue-500 text-blue-600 hover:bg-blue-50'}">
									{#if dirCredConfigured}
										<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 9.9-1"></path></svg>
										DIR Unlocked
									{:else}
										<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
										DIR Login
									{/if}
								</button>
								<span class="text-sm {dirCredConfigured ? 'text-green-700 font-medium' : 'text-gray-600'}">
									{dirCredConfigured ? `Unthrottled — using ${dirCredUsername}` : 'Public portal (throttled)'}
								</span>
							</div>
						</div>
						
						<div class="input inline-block w-Full">
							<!--<legend class="my-auto pb-2 w-Full">Databases:</legend>-->

							{#if hiddenFeature}
							<div class="tooltip-div">
								<div class="flex my-auto text-lg space-x-4 {aiFeature ? '' : 'hover:cursor-not-allowed'} {aiError === '' ? '' : 'border-red-600'}">
									<input id="ai-PR-mobile" name="ai_PR" bind:checked={aiPR} disabled={!aiFeature} type="checkbox" class="w-5"/>
									<label for="ai-PR-mobile" class="{aiFeature  ? '' : 'text-gray-500'}">ai.PR Search (in dev.)</label>
									<p class='error-text {aiError === '' ? 'hidden' : ''}'>{aiError}</p>
								</div>
								<p class='tooltip'>ai-simulated payroll.</p>
							</div>

							<div class="tooltip-div">
								<div class="flex my-auto text-lg space-x-4 {sensoryFeature ? '' : 'hover:cursor-not-allowed'} {sensoryError === '' ? '' : 'border-red-600'}">
									<input id="sensory-PR-mobile" name="sensory_PR" bind:checked={sensoryPR} disabled={!sensoryFeature} type="checkbox" class="w-5"/>
									<label for="sensory-PR-mobile" class="{sensoryFeature ? '' : 'text-gray-500'}">sensory.PR Search (in dev.)</label>
									<p class='error-text {sensoryError === '' ? 'hidden' : ''}'>{sensoryError}</p>
								</div>
								<p class='tooltip'>sensory-based recon. of payroll.</p>
							</div>
							{/if}

							
							<!--OPTIONS-->

							<div class="input inline-block w-Full">
								<div class="tooltip-div">
									<legend class="my-auto pb-2 w-Full">Base Options: </legend>
								</div>

								{#if hiddenFeature}
								<!--always on-->
								<div class="flex my-auto text-lg space-x-4 {useV2ParserError === '' ? '' : 'border-red-600'}">
									<input id="use-v2-parser-mobile" name="use_v2_parser" bind:checked={useV2Parser} type="checkbox" class="w-5"/>
									<label for="use-v2-parser-mobile">v2 Parser</label>
									<p class='error-text {useV2ParserError === '' ? 'hidden' : ''}'>{useV2ParserError}</p>
								</div>
								<div class="flex my-auto text-lg space-x-4 {includePDFsError === '' ? '' : 'border-red-600'}">
									<input id="include-pdfs-mobile" name="include_pdfs" bind:checked={includePDFs} type="checkbox" class="w-5"/>
									<label for="include-pdfs-mobile">Include PDFs</label>
									<p class='error-text {includePDFsError === '' ? 'hidden' : ''}'>{includePDFsError}</p>
								</div>
								{/if}
							</div>

						</div>
					</form>
				</div>

				<div class="{searchState === 'preview-data' ? '' : 'hidden'}">
					<p class="text-2xl font-semibold text-blue-900">Preview CPR Documents</p>
					<p class="text-lg font-semibold text-blue-900 mb-4">Tap a card to expand.</p>
					<p class="text-red-500 {otherError === '' ? '' : 'text-xl pb-5'}">{otherError}</p>
					<p class="text-blue-600 {previewDataNotice === '' ? '' : 'text-xl pb-5'}">{previewDataNotice}</p>
					<!-- Same count wording as the desktop layout (total matches +
					     hidden-NPS parenthetical) so both layouts report one number. -->
					<p class="text-lg mb-3">
						{totalPreviewResults.toLocaleString()} {totalPreviewResults == 1 ? "match" : "matches"} found
						{#if previewData.length !== displayedPreviewData.length}
							({previewData.length - displayedPreviewData.length} non-performance hidden)
						{/if}
					</p>
					{#if previewData.length < totalPreviewResults}
						<p class="text-orange-600 mb-4">
							Showing first {previewData.length} results in preview. Download full results for complete data.
						</p>
					{/if}
					<div class="mb-3 flex items-center">
						<input type="checkbox" id="hide-nps-mobile" class="w-4 h-4 mr-2" bind:checked={hideNPS} />
						<label for="hide-nps-mobile">Hide Non-Performance Statements</label>
					</div>
					<div class="space-y-3 max-h-[50vh] overflow-y-auto">
						{#each displayedPreviewData as row}
							<div class="border rounded-lg p-4 bg-white shadow-sm cursor-pointer"
								on:click={() => { if (expandedArr.includes(row.id)) { expandedArr = expandedArr.filter(id => id !== row.id); } else { expandedArr = [...expandedArr, row.id]; } }}>
								<input type="checkbox" class="w-5 h-5 mr-2 float-left" checked={selectedIds.has(row.id)}
									on:click|stopPropagation={() => toggleSelected(row.id)} />
								<p class="font-semibold text-blue-900 text-base">{row.contractorName}</p>
								<p class="text-sm text-gray-600">{row.forWeekEnding} &middot; Payroll #{row.payrollNum}{row.payrollAmendNum && row.payrollAmendNum !== '0' ? `-${row.payrollAmendNum}` : ''}{row.bundle_record ? '-bundle' : ''}</p>
								<div class="mt-2">
									<span class="text-sm text-gray-500">{row.projectID}</span>
									{#if row.contractorPWCR}
										<span class="text-sm text-gray-500">&middot; PWCR {row.contractorPWCR}</span>
									{/if}
								</div>
								{#if expandedArr.includes(row.id)}
									<div class="mt-3 pt-3 border-t">
										<p class="text-sm text-gray-700">Project: {row.projectName}</p>
										<p class="text-sm text-gray-700">Awarding Body: {row.awardingBody}</p>
										{#if row.fileId}
											<button class="mt-2 px-4 py-2 rounded-md bg-blue-500 hover:bg-blue-700 text-white text-sm font-semibold"
												on:click|stopPropagation={() => window.open(`/api/v1/b2/download/${row.fileId}.pdf`, '_blank', 'noopener')}>
												Preview PDF File
											</button>
										{:else}
											<button class="mt-2 px-4 py-2 rounded-md bg-gray-300 text-gray-600 text-sm font-semibold" disabled>Submit Search first</button>
										{/if}
									</div>
								{/if}
							</div>
						{/each}
					</div>

					{#if totalPreviewResults > 8000}
						<p class="text-red-600 font-bold text-lg">Warning: This search matched {totalPreviewResults.toLocaleString()} records and may take a very long time. Consider narrowing your date range or adding filters.</p>
					{/if}
					{#if feeLiable}
						<p class="mt-4 text-lg text-blue-900 font-semibold text-center">
							Download fee: up to ${approvedFee} ({approvedFileCount.toLocaleString()} files &times; $0.10)
						</p>
						<p class="text-sm text-gray-600 text-center">
							Billed to your card on the monthly invoice. You pay only for files delivered — never more than this maximum.
						</p>
					{/if}
					<div class="mt-6 flex flex-col space-y-3 items-center">
						<div class="relative inline-block">
							<div class="flex">
								<button on:click={() => { useOCR = false; handleSearch(); }} disabled={searchSubmit || (!scrapePDFsFeature && !feeLiable)}
									class="px-6 py-2 text-xl rounded-l-md {(scrapePDFsFeature || feeLiable) ? 'bg-blue-500 hover:bg-blue-700 cursor-pointer' : 'bg-gray-400 cursor-not-allowed'} disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold"
									title={(scrapePDFsFeature || feeLiable) ? '' : 'CSV Report feature required'}>
									{feeLiable ? 'Accept Fee & Download' : 'Download Records'}
								</button>
								<button type="button" disabled={searchSubmit || !scrapePDFsFeature}
									on:click={() => showSubmitDropdown = !showSubmitDropdown}
									class="px-2 py-2 text-xl rounded-r-md {scrapePDFsFeature ? 'bg-blue-600 hover:bg-blue-800 cursor-pointer' : 'bg-gray-400 cursor-not-allowed'} disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold">
									&#9660;
								</button>
							</div>
							{#if showSubmitDropdown}
								<div class="absolute right-0 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg z-50">
									<button type="button" class="w-full text-left px-4 py-2 text-blue-700 hover:bg-gray-100 text-base font-medium"
										on:click={() => { useOCR = true; showSubmitDropdown = false; handleSearch(); }}>
										OCR Deep Extract
									</button>
								</div>
							{/if}
						</div>
						<button type="button" on:click={newSearch} class="px-6 py-2 text-xl rounded-md bg-blue-500 hover:bg-blue-700 cursor-pointer text-white font-semibold">
							New Search
						</button>
					</div>
				</div>
				<div class="{searchState === 'aggregating-results' ? '' : 'hidden'}">
					<p class="text-2xl font-semibold text-blue-900">{searchStatus === 'finished' ? 'Results Ready to Download' : 'Aggregating Results: See History to Download'}</p>
					<p class="text-red-500 {otherError === '' ? '' : 'text-xl pb-5'}">{otherError}</p>
					{#if bundleNotice}
						<div class="bg-amber-50 border-l-4 border-amber-400 p-3 mb-3 text-sm text-amber-900">
							<p><strong>{bundleNotice.includes('Bundle mode canceled') ? 'Bundle download canceled.' : 'Bundle download active.'}</strong> {@html boldNoticeTimes(bundleNotice)}</p>
							<p class="mt-1">Your download will contain approximately 1/20th the number of files — each bundle file combines ~20 records into one PDF.</p>
						</div>
					{/if}
					{#if statusWarning && statusWarning !== bundleNotice}
						<p class="text-amber-600 text-lg pb-3">{statusWarning}</p>
					{/if}
					<div class="mt-5 space-y-3">
						<p class="text-xl font-semibold">Search ID: {searchId}</p>
						<p class="text-xl font-semibold">Status: {capitalizeFirstLetter(searchStatus)}</p>
						{#if searchStatus === 'downloading'}
							{#if typeof totalFiles === 'number' && totalFiles > 0}
								<p class="text-xl font-semibold">Files downloaded: {downloadedFiles} from {totalFiles} records</p>
							{:else if dirScopeTotal > 0}
								<p class="text-xl font-semibold">Files downloaded: {downloadedFiles} of {dirScopeTotal.toLocaleString()}</p>
							{:else if estimatedTotalFiles > 0}
								<p class="text-xl font-semibold">Files downloaded: {downloadedFiles} of ~{estimatedTotalFiles.toLocaleString()} (estimate)</p>
							{:else}
								<p class="text-xl font-semibold">Files downloaded: {downloadedFiles} &mdash; total being determined</p>
							{/if}
							<p class="text-lg text-gray-600 italic">{blurbText}</p>
							{#if downloadedFiles > 2 && totalFiles > 0}
							<p class="text-xl text-gray-700"><strong>~{Math.ceil(((Date.now() - downloadStartTime) / 60000 / downloadedFiles) * (totalFiles - downloadedFiles))} min remaining</strong></p>
							<p class="text-sm text-gray-500 mt-1">Runs ~2× faster outside CA business hours (6 PM–6 AM & weekends).</p>
						{/if}
					{:else if searchStatus === 'finished'}
						{#if feeLiable && acceptedFileCount > 0}
							<p class="mt-3 text-lg text-blue-900 font-semibold">
								Fee: ${(Math.min(deliveredFileCount, acceptedFileCount) * PER_FILE_FEE).toFixed(2)}
								for {Math.min(deliveredFileCount, acceptedFileCount).toLocaleString()} files delivered
								(accepted maximum: ${(acceptedFileCount * PER_FILE_FEE).toFixed(2)})
							</p>
						{/if}
						<div class="space-x-2 mt-5">
								{#if scrapePDFsFeature || feeLiable}
									<a href="{getGETAPILink('ecpr-search/download', {'search_id': searchId})}" download="{searchId}.zip" target="_self">
									<button class="w-5/12 pl-0 pb-2 pt-2 text-xl rounded-md bg-blue-500 hover:bg-blue-700 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
										Download
									</button></a>
								{:else}
									<button disabled class="w-5/12 pl-0 pb-2 pt-2 text-xl rounded-md bg-gray-400 cursor-not-allowed text-gray-200 font-semibold border-none shadow-sm"
										title="CSV Report feature required for download">
										Download
									</button>
								{/if}
								<button on:click={newSearch}
									class="w-6/12 pl-0 pb-2 pt-2 text-xl rounded-md bg-blue-500 hover:bg-blue-700 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
									New Search
								</button>
							</div>
						{/if}
						{#if (searchStatus !== 'finished') && (searchStatus !== 'error')}
							<div class="inline-block w-full mt-5">
								<button on:click={() => cancelSearch(searchId)} disabled={cancelSubmit}
									class="w-5/12 pl-0 pb-2 pt-1 text-xl rounded-md bg-blue-500 hover:bg-blue-700 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
								Cancel</button>
							</div>
						{:else if searchStatus === 'error'}
							<div class="inline-block w-full mt-5">
								<button on:click={newSearch}
									class="w-2/12 pl-0 pb-2 pt-2 text-xl rounded-md bg-blue-500 hover:bg-blue-700 cursor-pointer text-gray-100 font-semibold border-none shadow-sm">
								New Search</button>
							</div>
						{/if}
					</div>
				</div>
			</div>
		</div>
	</div>
	<Footer/>
</main>

<style>
	main.background { 
		background-color:theme("colors.gray.100");
	}
	.input-field {
		@apply rounded-md shadow-sm font-thin py-1 px-2;
	}

	.bc-sliver {
		width: 0.5rem;
		min-width: 0.5rem;
		padding: 0;
		border: none;
		background: transparent;
		outline: none;
		box-shadow: none;
		color: #1e3a8a;
		font-weight: 600;
		text-align: center;
	}

	/* Database Cylinder Graphics */
	.database-selector-container {
		margin-bottom: 1rem;
		padding: 0.5rem;
		background: transparent;
		border-radius: 0px;
		border: none;
	}

	.database-icons-wrapper {
		display: flex;
		gap: 0.75rem;
		justify-content: center;
		align-items: center;
	}

	.database-cylinder {
		position: relative;
		display: flex;
		flex-direction: column;
		align-items: center;
		padding: 0.25rem 0.5rem;
		min-width: 200px;
		background: #ffffff;
		border: 3px solid #cbd5e1;
		border-radius: 6px;
		cursor: pointer;
		transition: all 0.3s ease;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	}

	.database-cylinder:hover:not(:disabled) {
		transform: translateY(-2px);
		box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
		border-color: #3b82f6;
	}

	.database-cylinder:disabled {
		opacity: 0.4;
		cursor: not-allowed;
		background: #f1f5f9;
	}

	/* Active/Selected State - "Light Up" Effect */
	.database-cylinder.active {
		background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
		border-color: #1d4ed8;
		box-shadow: 0 0 20px rgba(59, 130, 246, 0.6),
					0 0 40px rgba(59, 130, 246, 0.4),
					0 8px 16px rgba(0, 0, 0, 0.2);
		animation: pulse-glow 2s ease-in-out infinite;
	}

	.database-cylinder.active .cylinder-label,
	.database-cylinder.active .database-icon {
		color: #ffffff;
	}

	@keyframes pulse-glow {
		0%, 100% {
			box-shadow: 0 0 20px rgba(59, 130, 246, 0.6),
						0 0 40px rgba(59, 130, 246, 0.4),
						0 8px 16px rgba(0, 0, 0, 0.2);
		}
		50% {
			box-shadow: 0 0 30px rgba(59, 130, 246, 0.8),
						0 0 60px rgba(59, 130, 246, 0.6),
						0 12px 24px rgba(0, 0, 0, 0.25);
		}
	}

	.database-cylinder.active .cylinder-top {
		background: linear-gradient(180deg, #60a5fa 0%, #3b82f6 100%);
	}

	/* Cylinder Body */
	.cylinder-body {
		position: relative;
		width: 220px;
		padding: 0.15rem 0;
	}

	.database-icon {
		width: 140px;
		height: 22px;
		color: #475569;
		transition: all 0.3s ease;
	}

	.database-cylinder.active .database-icon {
		color: #ffffff;
		filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.5));
	}

	/* Labels */
	.cylinder-label {
		display: flex;
		flex-direction: row;
		align-items: baseline;
		gap: 0.75rem;
		margin-top: 0.5rem;
		text-align: center;
	}

	.db-title {
		font-size: 1.125rem;
		font-weight: 700;
		color: #1e293b;
		transition: color 0.3s ease;
	}

	.db-subtitle {
		font-size: 0.75rem;
		color: #64748b;
		margin-top: 0.125rem;
		transition: color 0.3s ease;
	}

	.database-cylinder.active .db-title {
		color: #ffffff;
	}

	.database-cylinder.active .db-subtitle {
		color: #e0e7ff;
	}

	/* Selection Badge (Checkmark) */
	.selection-badge {
		position: absolute;
		top: 0.125rem;
		right: 0.125rem;
		width: 16px;
		height: 16px;
		background: #10b981;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		color: white;
		font-weight: bold;
		font-size: 0.75rem;
		box-shadow: 0 2px 8px rgba(16, 185, 129, 0.4);
		animation: pop-in 0.3s ease;
	}

	@keyframes pop-in {
		0% { transform: scale(0); }
		50% { transform: scale(1.2); }
		100% { transform: scale(1); }
	}

	/* Compact variant: split-view side panel */
	.database-cylinder.compact {
		flex: 1 1 0;
		min-width: 0;
		flex-direction: row;
		justify-content: center;
		gap: 0.375rem;
		padding: 0.25rem 0.5rem;
	}

	.database-cylinder.compact .cylinder-body {
		width: auto;
		padding: 0;
	}

	.database-cylinder.compact .database-icon {
		width: 20px;
		height: 20px;
	}

	.database-cylinder.compact .cylinder-label {
		margin-top: 0;
	}

	.database-cylinder.compact .db-title {
		font-size: 0.875rem;
	}

	.database-cylinder.compact .selection-badge {
		width: 14px;
		height: 14px;
		font-size: 0.625rem;
	}

	/* Mobile Responsive */
	@media (max-width: 640px) {
		.database-icons-wrapper {
			display: grid;
			grid-template-columns: 1fr 1fr;
			gap: 0.4rem;
			padding: 0 1rem; /* Add side padding */
		}
		
		.database-cylinder {
			min-width: auto;
			width: 100%;
			max-width: 160px; /* Limit max width */
			padding: 0.25rem 0.5rem;
			border-width: 2px; /* Thinner border */
		}
		
		/* Smaller icon on mobile */
		.database-cylinder .database-icon {
			width: 80px;
			height: 18px;
		}
		
		/* Smaller text on mobile */
		.database-cylinder .db-title {
			font-size: 0.875rem; /* Smaller */
		}
		
		.database-cylinder .db-subtitle {
			font-size: 0.625rem; /* Smaller */
		}
		
		/* Smaller badge on mobile */
		.database-cylinder .selection-badge {
			width: 14px;
			height: 14px;
			font-size: 0.625rem;
		}
	}
</style>